import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  LinearProgress,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  BlurOn as BlurOnIcon,
  BarChart as BarChartIcon,
  TrendingUp as TrendingUpIcon,
  WarningAmber as WarningIcon,
  InfoOutlined as InfoIcon,
  UploadFile as UploadIcon,
  Fullscreen as FullscreenIcon,
  ArrowDropDown as ArrowDropDownIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import useCustomAuth from '../../Context/Hooks/useCustomAuth';

import { storeAPI } from '../../API/storeAPI';
import { deviceAPI } from '../../API/deviceAPI';
import { heatmapAPI } from '../../API/heatmapAPI';
import useGatewayContagem from '../../Context/Hooks/useGatewayContagem';
import PageHeader from '../../Components/GatewayContagem/PageHeader';
import DateRangePopover from '../../Components/GatewayContagem/DateRangePopover';
import VegaTrafficChart from '../../Components/GatewayContagem/VegaTrafficChart';
import AutoCadAlignModal from '../../Components/Modal/AutoCadAlignModal';
import HeatmapFullscreenDialog from './Components/HeatmapFullscreenDialog';
import { filterHistoricalZones, formatTemporalWindow } from './Helpers/heatmapTemporalHelpers';
import { fmtNum, fmtDate, toUtcStartOfDay, toUtcEndOfDay } from '../../Utils/format';
import {
  CANVAS_W,
  CANVAS_H,
  CANVAS_MAX_W_DEFAULT,
  CANVAS_MAX_W_XL,
  CANVAS_MIN_H,
  CANVAS_MAX_H,
  CANVAS_VIEWPORT_OFFSET_H,
  BADGE_COLORS,
  drawArrowhead,
  formatDwellTime,
  getAgeLabelsAndValues,
  hitTestZones,
} from './Helpers/heatmapHelpers';

const PERIODS = ['today', '7d', '30d', 'month', 'custom', 'allTime'];
const PERIOD_KEY = { today: 'today', '7d': 'sevenDays', '30d': 'thirtyDays', month: 'month', custom: 'custom', allTime: 'allTime' };

const PeriodToggle = ({ value, onChange, t, flipped = false }) => (
  <ToggleButtonGroup
    value={value}
    exclusive
    onChange={(_, v) => v && onChange(v)}
    size="small"
    sx={{
      '& .MuiToggleButton-root': !flipped ? {
        px: 1.5,
        py: 0.5,
        fontSize: '0.8125rem',
        fontWeight: 500,
        textTransform: 'none',
        border: '1px solid',
        borderColor: 'divider',
        color: 'text.secondary',
        bgcolor: 'background.paper',
        '&:hover': { bgcolor: 'action.hover' },
        '&.Mui-selected': {
          bgcolor: 'primary.dark',
          color: '#fff',
          borderColor: 'primary.dark',
          '&:hover': { bgcolor: 'primary.main' },
        },
      } : {
        px: 1.5,
        py: 0.5,
        fontSize: '0.8125rem',
        fontWeight: 600,
        textTransform: 'none',
        border: '1px solid',
        borderColor: 'primary.dark',
        bgcolor: 'primary.dark',
        color: '#ffffff',
        '&:hover': { bgcolor: 'primary.main', color: '#ffffff' },
        '&.Mui-selected': {
          bgcolor: '#ffffff',
          color: 'primary.dark',
          borderColor: 'primary.dark',
          fontWeight: 700,
          '&:hover': { bgcolor: '#f5f5f5', color: 'primary.dark' },
        },
      },
    }}
  >
    {PERIODS.map((p) => (
      <ToggleButton key={p} value={p}>
        {t(`period.${PERIOD_KEY[p]}`, p)}
      </ToggleButton>
    ))}
  </ToggleButtonGroup>
);

// ─── Canvas Heatmap Renderer ──────────────────────────────────────────────────
const BADGE_FALLBACK = { solid: 'rgba(158, 158, 158, 1)', half: 'rgba(158, 158, 158, 0.50)', hex: '#9E9E9E' };

function getBadgeColor(logicType) {
  if (!logicType) return BADGE_FALLBACK;
  return BADGE_COLORS[logicType.toUpperCase()] || BADGE_FALLBACK;
}

function resolveBadgePositions(realZones, W, H) {
  if (!Array.isArray(realZones)) return [];
  const badges = [];
  const badgeR = Math.max(10, Math.round(W / 60));
  const spacing = 4; // Spacing between side-by-side badges

  // 1. Calculate initial centroids
  let currentBadgeNum = 1;
  for (let i = 0; i < realZones.length; i++) {
    const zone = realZones[i];
    if (zone.xovisLogicType !== 'GENERAL') {
      if (zone.vertices && zone.vertices.length >= 2) {
        let sx = 0;
        let sy = 0;
        for (const v of zone.vertices) { sx += v.nx; sy += v.ny; }
        badges.push({
          idx: i,
          badgeNum: currentBadgeNum++,
          cx: (sx / zone.vertices.length) * W,
          cy: (sy / zone.vertices.length) * H,
          r: badgeR,
          zone,
        });
      }
    }
  }

  // 2. Group badges that share the same centroid (within 1 pixel)
  const centroidGroups = [];
  const visited = new Set();

  for (let i = 0; i < badges.length; i++) {
    if (!visited.has(i)) {
      const group = [badges[i]];
      visited.add(i);

      for (let j = i + 1; j < badges.length; j++) {
        if (!visited.has(j)) {
          const dx = badges[i].cx - badges[j].cx;
          const dy = badges[i].cy - badges[j].cy;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 1.0) {
            group.push(badges[j]);
            visited.add(j);
          }
        }
      }
      centroidGroups.push(group);
    }
  }

  // 3. For each group with more than 1 badge, layout them horizontally side-by-side centered at the centroid
  for (const group of centroidGroups) {
    if (group.length > 1) {
      const n = group.length;
      const diameter = 2 * badgeR;
      const totalWidth = n * diameter + (n - 1) * spacing;
      const centroidX = group[0].cx;
      // Start X position offset
      const startX = centroidX - totalWidth / 2 + badgeR;
      for (let k = 0; k < n; k++) {
        group[k].cx = startX + k * (diameter + spacing);
      }
    }
  }

  // 4. Run the iterative push pass to resolve general overlaps with other geometries
  for (let pass = 0; pass < 5; pass++) {
    for (let a = 0; a < badges.length; a++) {
      for (let b = a + 1; b < badges.length; b++) {
        const dx = badges[b].cx - badges[a].cx;
        const dy = badges[b].cy - badges[a].cy;
        let dist = Math.sqrt(dx * dx + dy * dy);
        const minDist = badges[a].r + badges[b].r + 4;
        if (dist < minDist) {
          if (dist < 0.1) {
            // Prevent division by zero if they are still perfectly on top of each other
            badges[b].cx += 1;
            dist = 1;
          }
          const overlap = (minDist - dist) / 2;
          const nx = dx / dist;
          const ny = dy / dist;
          badges[a].cx -= nx * overlap;
          badges[a].cy -= ny * overlap;
          badges[b].cx += nx * overlap;
          badges[b].cy += ny * overlap;
        }
      }
    }
  }

  return badges;
}

let heatmapColorPalette = null;
function getHeatmapColorPalette() {
  if (heatmapColorPalette) return heatmapColorPalette;

  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 1;
  const ctx = canvas.getContext('2d');
  const grad = ctx.createLinearGradient(0, 0, 256, 0);

  // Blueish (low intensity) -> Red (high intensity)
  grad.addColorStop(0.0, 'rgba(0, 0, 255, 0)'); // Transparent
  grad.addColorStop(0.15, 'rgba(0, 0, 255, 0.3)'); // Dark Blue
  grad.addColorStop(0.3, 'rgba(0, 255, 255, 0.65)'); // Cyan
  grad.addColorStop(0.5, 'rgba(0, 255, 0, 0.8)'); // Green
  grad.addColorStop(0.75, 'rgba(255, 255, 0, 0.9)'); // Yellow
  grad.addColorStop(0.9, 'rgba(255, 128, 0, 0.95)'); // Orange
  grad.addColorStop(1.0, 'rgba(255, 0, 0, 1)'); // Red

  ctx.style = 'image-rendering: pixelated';
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 256, 1);
  heatmapColorPalette = ctx.getImageData(0, 0, 256, 1).data;
  return heatmapColorPalette;
}

function drawHeatmap(canvas, hotspots, realZones, bgImage) {
  const ctx = canvas.getContext('2d');
  const W = canvas.width;
  const H = canvas.height;
  const dpr = window.devicePixelRatio || 1;

  // 1. Clear to transparent
  ctx.globalCompositeOperation = 'source-over';
  ctx.clearRect(0, 0, W, H);

  // 2. Draw soft grayscale density blobs
  ctx.globalCompositeOperation = 'lighter';
  for (const hs of hotspots) {
    const cx = hs.cx * W;
    const cy = hs.cy * H;
    const r = hs.r * Math.min(W, H);
    const { intensity } = hs;

    const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
    g.addColorStop(0, `rgba(0, 0, 0, ${intensity})`);
    g.addColorStop(1, 'rgba(0, 0, 0, 0)');

    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
  }

  // 3. Colorize the alpha values using the blue-to-red gradient palette
  const palette = getHeatmapColorPalette();
  const imgData = ctx.getImageData(0, 0, W, H);
  const { data } = imgData;
  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3];
    if (a > 0) {
      const offset = a * 4;
      data[i] = palette[offset]; // Red
      data[i + 1] = palette[offset + 1]; // Green
      data[i + 2] = palette[offset + 2]; // Blue
      data[i + 3] = Math.min(130, a * 0.50); // Alpha
    }
  }
  ctx.putImageData(imgData, 0, 0);

  // 4. Paint the background behind the heatmap
  ctx.globalCompositeOperation = 'destination-over';
  if (bgImage) {
    ctx.drawImage(bgImage, 0, 0, W, H);
  } else {
    ctx.fillStyle = '#0B1D35';
    ctx.fillRect(0, 0, W, H);
  }

  // 5. Draw real zone geometries (lines or polygons)
  ctx.globalCompositeOperation = 'source-over';

  if (Array.isArray(realZones)) {
    // 1. Draw all geometry lines/borders first
    for (const zone of realZones) {
      if (zone.vertices && zone.vertices.length >= 2) {
        ctx.beginPath();
        ctx.moveTo(zone.vertices[0].nx * W, zone.vertices[0].ny * H);
        for (let i = 1; i < zone.vertices.length; i++) {
          ctx.lineTo(zone.vertices[i].nx * W, zone.vertices[i].ny * H);
        }

        // Add a premium drop shadow under lines/strokes to stand out on bright yellow
        ctx.shadowColor = 'rgba(0, 0, 0, 0.65)';
        ctx.shadowBlur = 4 * dpr;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 2 * dpr;

        const isZone = (zone.geometryType || '').toUpperCase() === 'ZONE';

        if (isZone) {
          ctx.closePath();
          ctx.fillStyle = 'rgba(255, 110, 64, 0.05)';
          ctx.fill();

          ctx.strokeStyle = 'rgba(255, 110, 64, 0.90)'; // Premium orange-red for zones
          ctx.lineWidth = 2.5 * dpr;
          ctx.setLineDash([]);
          ctx.stroke();
        } else {
          ctx.strokeStyle = 'rgba(0, 229, 255, 0.95)'; // Premium electric cyan for crossing lines
          ctx.lineWidth = 2.5 * dpr;
          ctx.setLineDash([8 * dpr, 6 * dpr]); // Crisp dotted/dashed layout
          ctx.stroke();

          if (zone.vertices && zone.vertices.length >= 2) {
            const x0 = zone.vertices[0].nx * W;
            const y0 = zone.vertices[0].ny * H;
            const x1 = zone.vertices[1].nx * W;
            const y1 = zone.vertices[1].ny * H;
            if (zone.xovisLogicType && zone.xovisLogicType.includes('ATTENTION')) {
              drawArrowhead(ctx, x0, y0, x1, y1, dpr);
            }
          }
        }

        // Reset shadow
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
      }
    }

    // 2. Resolve badge positions to avoid overlap, and draw them ONCE
    const resolvedBadges = resolveBadgePositions(realZones, W, H);
    ctx.setLineDash([]);

    for (const b of resolvedBadges) {
      const colors = getBadgeColor(b.zone.xovisLogicType);

      // Badge circle — 50% transparent body
      ctx.beginPath();
      ctx.arc(b.cx, b.cy, b.r, 0, Math.PI * 2);
      ctx.fillStyle = colors.half;
      ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,0.85)';
      ctx.lineWidth = 1.5 * dpr;
      ctx.stroke();

      // Badge number
      ctx.fillStyle = '#FFFFFF';
      ctx.font = `700 ${Math.max(9, Math.round(b.r * 0.95))}px Inter, Poppins, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(String(b.badgeNum), b.cx, b.cy + 0.5);
    }
  }
}

const HeatmapCanvas = ({
  hotspots,
  realZones,
  bgImage,
  gridWidth = 45,
  gridHeight = 25,
  hoveredZoneIdx,
  onZoneHover,
  isFullScreen = false,
}) => {
  const { t } = useTranslation('heatmap');
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const wrapperRef = useRef(null);
  const zoneRegionsRef = useRef([]);

  // Build zone hit-test regions whenever realZones or canvas size changes
  const buildZoneRegions = useCallback((W, H) => {
    if (!Array.isArray(realZones)) { zoneRegionsRef.current = []; return; }
    const dpr = window.devicePixelRatio || 1;
    const resolvedBadges = resolveBadgePositions(realZones, W, H);
    const regions = [];
    realZones.forEach((zone, idx) => {
      if (!zone.vertices || zone.vertices.length < 2) return;
      const points = zone.vertices.map(v => ({ x: v.nx * W, y: v.ny * H }));
      const isZone = (zone.geometryType || '').toUpperCase() === 'ZONE';
      // Find the resolved badge position for this zone
      const b = resolvedBadges.find(x => x.idx === idx);
      const cx = b ? b.cx : (points[0].x);
      const cy = b ? b.cy : (points[0].y);
      const badgeR = Math.max(10, Math.round(W / 60 / dpr));
      const segments = [];
      for (let i = 0; i < points.length - 1; i++) {
        segments.push({ x1: points[i].x / dpr, y1: points[i].y / dpr, x2: points[i + 1].x / dpr, y2: points[i + 1].y / dpr });
      }
      if (isZone) {
        segments.push({ x1: points[points.length - 1].x / dpr, y1: points[points.length - 1].y / dpr, x2: points[0].x / dpr, y2: points[0].y / dpr });
      }
      regions.push({
        index: idx,
        isZone,
        polygon: isZone ? points.map(p => ({ x: p.x / dpr, y: p.y / dpr })) : [],
        segments: isZone ? [] : segments,
        badgeCx: cx / dpr,
        badgeCy: cy / dpr,
        badgeR,
        zone,
      });
    });
    zoneRegionsRef.current = regions;
  }, [realZones]);

  // Draw the hover overlay (glow + tooltip)
  const drawOverlay = useCallback((hoverIdx) => {
    const overlay = overlayRef.current;
    const baseCanvas = canvasRef.current;
    if (!overlay || !baseCanvas) return;
    const dpr = window.devicePixelRatio || 1;
    const ctx = overlay.getContext('2d');
    ctx.clearRect(0, 0, overlay.width, overlay.height);
    if (hoverIdx < 0 || !Array.isArray(realZones) || hoverIdx >= realZones.length) return;

    const zone = realZones[hoverIdx];
    if (!zone?.vertices || zone.vertices.length < 2) return;
    const W = overlay.width;
    const H = overlay.height;
    const isZone = (zone.geometryType || '').toUpperCase() === 'ZONE';
    const glowColor = isZone ? 'rgba(255, 110, 64, 1)' : 'rgba(0, 229, 255, 1)';
    const fillColor = isZone ? 'rgba(255, 110, 64, 0.15)' : 'rgba(0, 229, 255, 0.10)';

    // Draw highlighted geometry
    ctx.beginPath();
    ctx.moveTo(zone.vertices[0].nx * W, zone.vertices[0].ny * H);
    for (let i = 1; i < zone.vertices.length; i++) {
      ctx.lineTo(zone.vertices[i].nx * W, zone.vertices[i].ny * H);
    }
    if (isZone) ctx.closePath();

    ctx.shadowColor = glowColor;
    ctx.shadowBlur = 16 * dpr;
    ctx.strokeStyle = glowColor;
    ctx.lineWidth = 3.5 * dpr;
    ctx.setLineDash(isZone ? [] : [8 * dpr, 6 * dpr]);
    ctx.stroke();
    if (isZone) {
      ctx.fillStyle = fillColor;
      ctx.fill();
    } else {
      const x0 = zone.vertices[0].nx * W;
      const y0 = zone.vertices[0].ny * H;
      const x1 = zone.vertices[1].nx * W;
      const y1 = zone.vertices[1].ny * H;
      if (zone.xovisLogicType && zone.xovisLogicType.includes('ATTENTION')) {
        drawArrowhead(ctx, x0, y0, x1, y1, dpr);
      }
    }
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.setLineDash([]);

    // Draw enlarged badge
    const resolvedBadges = resolveBadgePositions(realZones, W, H);
    const b = resolvedBadges.find(x => x.idx === hoverIdx);
    const cx = b ? b.cx : (zone.vertices[0].nx * W);
    const cy = b ? b.cy : (zone.vertices[0].ny * H);
    const badgeR = Math.max(13, Math.round(W / 48));
    const colors = getBadgeColor(zone.xovisLogicType);
    const glowSolid = colors.solid;

    ctx.beginPath();
    ctx.arc(cx, cy, badgeR, 0, Math.PI * 2);
    ctx.fillStyle = colors.half;
    ctx.fill();
    ctx.strokeStyle = '#FFFFFF';
    ctx.lineWidth = 2 * dpr;
    ctx.stroke();

    // Solid ring on hover to make it pop
    ctx.beginPath();
    ctx.arc(cx, cy, badgeR + 2, 0, Math.PI * 2);
    ctx.strokeStyle = glowSolid;
    ctx.lineWidth = 1.5 * dpr;
    ctx.stroke();

    ctx.fillStyle = '#FFFFFF';
    ctx.font = `700 ${Math.max(11, Math.round(badgeR * 0.9))}px Inter, Poppins, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(String(b ? b.badgeNum : ''), cx, cy + 0.5);

    // Draw floating tooltip label
    const tooltipText = zone.name;
    ctx.font = `600 ${Math.max(11, Math.round(W / 65))}px Inter, Poppins, sans-serif`;
    const tw = ctx.measureText(tooltipText).width;
    const tpx = 8;
    const tpy = 5;
    const tbW = tw + tpx * 2;
    const tbH = 18 + tpy * 2;
    let tx = cx + badgeR + 8;
    let ty = cy - tbH / 2;
    // Keep tooltip inside canvas
    if (tx + tbW > W) tx = cx - badgeR - 8 - tbW;
    if (ty < 4) ty = 4;
    if (ty + tbH > H) ty = H - tbH - 4;

    // Tooltip background
    ctx.fillStyle = 'rgba(11, 29, 53, 0.88)';
    const r = 4 * dpr;
    ctx.beginPath();
    ctx.moveTo(tx + r, ty);
    ctx.lineTo(tx + tbW - r, ty);
    ctx.quadraticCurveTo(tx + tbW, ty, tx + tbW, ty + r);
    ctx.lineTo(tx + tbW, ty + tbH - r);
    ctx.quadraticCurveTo(tx + tbW, ty + tbH, tx + tbW - r, ty + tbH);
    ctx.lineTo(tx + r, ty + tbH);
    ctx.quadraticCurveTo(tx, ty + tbH, tx, ty + tbH - r);
    ctx.lineTo(tx, ty + r);
    ctx.quadraticCurveTo(tx, ty, tx + r, ty);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = glowColor;
    ctx.lineWidth = 1;
    ctx.stroke();

    // Tooltip text
    ctx.fillStyle = '#FFFFFF';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(tooltipText, tx + tpx, ty + tbH / 2);
  }, [realZones]);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    const repaint = () => {
      const canvas = canvasRef.current;
      const overlay = overlayRef.current;
      if (!canvas || !overlay || !wrapper) return;

      const dpr = window.devicePixelRatio || 1;
      const aspect = (gridWidth || 45) / (gridHeight || 25);

      // Available container width
      let availW = wrapper.parentElement ? wrapper.parentElement.clientWidth : wrapper.clientWidth;
      if (!isFullScreen) {
        availW = Math.min(availW || CANVAS_MAX_W_DEFAULT, window.innerWidth >= 1536 ? CANVAS_MAX_W_XL : CANVAS_MAX_W_DEFAULT);
      } else {
        availW = window.innerWidth;
      }

      // Max available height so tall heatmaps never exceed the viewport / card boundary
      const maxH = isFullScreen
        ? window.innerHeight
        : Math.max(CANVAS_MIN_H, Math.min(CANVAS_MAX_H, window.innerHeight - CANVAS_VIEWPORT_OFFSET_H));

      let cssW;
      let cssH;
      if (availW / aspect > maxH) {
        // Height is the constraint (tall heatmap or lower vertical resolution)
        cssH = Math.round(maxH);
        cssW = Math.round(cssH * aspect);
      } else {
        // Width is the constraint (wide heatmap)
        cssW = Math.round(availW);
        cssH = Math.round(cssW / aspect);
      }

      if (cssW <= 0 || cssH <= 0) return;

      wrapper.style.width = `${cssW}px`;
      wrapper.style.height = `${cssH}px`;

      canvas.width = cssW * dpr;
      canvas.height = cssH * dpr;
      canvas.style.width = `${cssW}px`;
      canvas.style.height = `${cssH}px`;

      overlay.width = cssW * dpr;
      overlay.height = cssH * dpr;
      overlay.style.width = `${cssW}px`;
      overlay.style.height = `${cssH}px`;

      if (hotspots && realZones) {
        drawHeatmap(canvas, hotspots, realZones, bgImage);
        buildZoneRegions(cssW * dpr, cssH * dpr);
      }
    };

    repaint();

    const handleResize = () => repaint();
    window.addEventListener('resize', handleResize);

    const ro = new ResizeObserver(repaint);
    if (wrapper.parentElement) {
      ro.observe(wrapper.parentElement);
    } else {
      ro.observe(wrapper);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      ro.disconnect();
    };
  }, [hotspots, realZones, bgImage, gridWidth, gridHeight, buildZoneRegions, isFullScreen]);

  // Redraw overlay when hoveredZoneIdx changes (from sidebar hover)
  useEffect(() => {
    drawOverlay(hoveredZoneIdx ?? -1);
  }, [hoveredZoneIdx, drawOverlay]);

  const handleMouseMove = useCallback((e) => {
    const overlay = overlayRef.current;
    if (!overlay) return;
    const { clientX, clientY } = e;
    const rect = overlay.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return;
    const dpr = window.devicePixelRatio || 1;
    const cssW = overlay.clientWidth || (overlay.width / dpr);
    const cssH = overlay.clientHeight || (overlay.height / dpr);
    const mx = (clientX - rect.left) * (cssW / rect.width);
    const my = (clientY - rect.top) * (cssH / rect.height);
    const hitIdx = hitTestZones(mx, my, zoneRegionsRef.current);
    if (onZoneHover) onZoneHover(hitIdx >= 0 ? hitIdx : null);
    drawOverlay(hitIdx);
    overlay.style.cursor = hitIdx >= 0 ? 'pointer' : 'default';
  }, [onZoneHover, drawOverlay]);

  const handleMouseLeave = useCallback(() => {
    if (onZoneHover) onZoneHover(null);
    drawOverlay(-1);
  }, [onZoneHover, drawOverlay]);

  const hasNoHotspots = !Array.isArray(hotspots) || hotspots.length === 0;

  return (
    <Box
      ref={wrapperRef}
      sx={{
        lineHeight: 0,
        position: 'relative',
        display: 'inline-block',
        ml: isFullScreen ? 'auto' : 0,
        mr: isFullScreen ? 'auto' : 'auto',
      }}
    >
      <canvas
        ref={canvasRef}
        style={{ display: 'block', borderRadius: 4 }}
      />
      <canvas
        ref={overlayRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{ display: 'block', borderRadius: 4, position: 'absolute', top: 0, left: 0 }}
      />
      {hasNoHotspots && (
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            bgcolor: 'rgba(11, 29, 53, 0.88)',
            backdropFilter: 'blur(8px)',
            border: '1px solid rgba(255, 255, 255, 0.18)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.35)',
            borderRadius: 2,
            px: 2.5,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            pointerEvents: 'none',
          }}
        >
          <WarningIcon sx={{ fontSize: 20, color: 'warning.main' }} />
          <Typography variant="caption" sx={{ color: '#fff', fontWeight: 500, letterSpacing: '0.01em' }}>
            {t('canvas.noDataForPeriod')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

HeatmapCanvas.propTypes = {
  hotspots: PropTypes.arrayOf(PropTypes.shape({
    cx: PropTypes.number,
    cy: PropTypes.number,
    r: PropTypes.number,
    intensity: PropTypes.number,
  })).isRequired,
  realZones: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    geometryType: PropTypes.string,
    vertices: PropTypes.arrayOf(PropTypes.shape({
      nx: PropTypes.number,
      ny: PropTypes.number,
    })),
  })).isRequired,
  bgImage: PropTypes.instanceOf(HTMLImageElement),
  gridWidth: PropTypes.number,
  gridHeight: PropTypes.number,
  hoveredZoneIdx: PropTypes.number,
  onZoneHover: PropTypes.func,
  isFullScreen: PropTypes.bool,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const isZoneLogic = (type) => {
  if (!type) return false;
  const t = type.toUpperCase();
  if (t.includes('ZONE_IN_OUT_COUNT')) return false;
  return t.includes('ZONE') || t.includes('ATTENTION') || t.includes('QUEUE');
};

// Matches DashboardPage / ContagensPage toggle button style exactly
const TOGGLE_SX = {
  '& .MuiToggleButton-root': {
    px: 1.5,
    py: 0.5,
    fontSize: '0.8125rem',
    fontWeight: 500,
    textTransform: 'none',
    border: '1px solid',
    borderColor: 'divider',
    color: 'text.secondary',
    '&.Mui-selected': {
      bgcolor: 'primary.dark',
      color: '#fff',
      borderColor: 'primary.dark',
      '&:hover': { bgcolor: 'primary.main' },
    },
  },
};

// ─── Page ─────────────────────────────────────────────────────────────────────

// ─── Heatmap Visual Tuning Configs ────────────────────────────────────────────
// Adjust these values to manually tune the heatmap intensity, radius, and noise filter
// Discard low-density noise (below 1.5% of max density)
const NOISE_THRESHOLD = 0;
// Hotspot radius in grid cell widths
const BLENDING_RADIUS = 2.2;
// Power curve exponent (lower exponent boosts faint spots, 1.0 = linear)
const INTENSITY_BOOST_EXPONENT = 0.45;
// Opacity multiplier factor (increases overall glow brightness)
const INTENSITY_MULTIPLIER = 1.25;

const HeatmapViewerPage = () => {
  const { t } = useTranslation('heatmap');
  const { selectedClient } = useGatewayContagem();
  const { user } = useCustomAuth();

  const isGatewayAdmin = user?.roles?.includes('GATEWAY_ADMIN') || user?.role === 'GATEWAY_ADMIN';
  const isClientAdmin = user?.roles?.includes('CLIENT_ADMIN') || user?.role === 'CLIENT_ADMIN';
  const canResetHeatmap = isGatewayAdmin || isClientAdmin;

  const [storeId, setStoreId] = useState('');
  const [deviceId, setDeviceId] = useState('');
  const [period, setPeriod] = useState('today');
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setUTCHours(0, 0, 0, 0);
    return d;
  });
  const [endDate, setEndDate] = useState(() => new Date());
  const [viewType, setViewType] = useState('densidade');
  const [isLoading, setIsLoading] = useState(false);

  // Dual Period Comparison states
  const [isCompareActive, setIsCompareActive] = useState(false);
  const [periodB, setPeriodB] = useState('custom');
  const [startDateB, setStartDateB] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return toUtcStartOfDay(d);
  });
  const [endDateB, setEndDateB] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return toUtcEndOfDay(d);
  });
  const [heatmapDataB, setHeatmapDataB] = useState(null);
  const [isLoadingB, setIsLoadingB] = useState(false);

  const [heatmapData, setHeatmapData] = useState(null);

  const [bgImage, setBgImage] = useState(null);
  const [bgImageVersion, setBgImageVersion] = useState(0);
  const [hoveredZoneIdx, setHoveredZoneIdx] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const zoneCardRefs = useRef({});

  // AutoCAD alignment states
  const [autoCadOpen, setAutoCadOpen] = useState(false);

  // Zone traffic chart states
  const zoneChartRef = useRef(null);
  const [allDeviceZones, setAllDeviceZones] = useState([]);
  const [zoneMetrics, setZoneMetrics] = useState([]);
  const [committedZoneMetrics, setCommittedZoneMetrics] = useState([]);
  const [selectedZoneIds, setSelectedZoneIds] = useState([]);
  const [heatmapZoneMenuOpen, setHeatmapZoneMenuOpen] = useState(false);
  const [granularity, setGranularity] = useState('HOURLY');
  const [stintMenuAnchor, setStintMenuAnchor] = useState(null);

  // Committed chart state to prevent phantom charts or intermediate flash
  const [committedPeriod, setCommittedPeriod] = useState('today');
  const [committedStartDate, setCommittedStartDate] = useState(() => toUtcStartOfDay(new Date()));
  const [committedEndDate, setCommittedEndDate] = useState(() => new Date());
  const [committedGranularity, setCommittedGranularity] = useState('HOURLY');

  // Sync Period A preset → startDate, endDate
  useEffect(() => {
    if (!period) {
      return;
    }
    if (period === 'allTime') {
      setStartDate(null);
      setEndDate(null);
      return;
    }
    if (period === 'custom') {
      return;
    }
    const now = new Date();
    const display = new Date();

    if (period === 'today') {
      display.setHours(0, 0, 0, 0);
      setStartDate(toUtcStartOfDay(display));
    } else if (period === '7d') {
      display.setDate(display.getDate() - 6);
      display.setHours(0, 0, 0, 0);
      setStartDate(toUtcStartOfDay(display));
    } else if (period === '30d') {
      display.setDate(display.getDate() - 29);
      display.setHours(0, 0, 0, 0);
      setStartDate(toUtcStartOfDay(display));
    } else if (period === 'month') {
      display.setDate(1);
      display.setHours(0, 0, 0, 0);
      setStartDate(toUtcStartOfDay(display));
    }

    setEndDate(now);
  }, [period]);

  // Sync Period B preset → startDateB, endDateB
  useEffect(() => {
    if (!periodB) {
      return;
    }
    if (periodB === 'allTime') {
      setStartDateB(null);
      setEndDateB(null);
      return;
    }
    if (periodB === 'custom') {
      return;
    }
    const now = new Date();
    const display = new Date();

    if (periodB === 'today') {
      display.setHours(0, 0, 0, 0);
      setStartDateB(toUtcStartOfDay(display));
    } else if (periodB === '7d') {
      display.setDate(display.getDate() - 6);
      display.setHours(0, 0, 0, 0);
      setStartDateB(toUtcStartOfDay(display));
    } else if (periodB === '30d') {
      display.setDate(display.getDate() - 29);
      display.setHours(0, 0, 0, 0);
      setStartDateB(toUtcStartOfDay(display));
    } else if (periodB === 'month') {
      display.setDate(1);
      display.setHours(0, 0, 0, 0);
      setStartDateB(toUtcStartOfDay(display));
    }

    setEndDateB(now);
  }, [periodB]);

  useEffect(() => {
    if (!deviceId) {
      setAllDeviceZones([]);
      return;
    }
    deviceAPI.getZones(deviceId).then(res => {
      const rawZones = Array.isArray(res?.value) ? res.value : [];
      const nonGeneral = rawZones.filter(z => (z.xovisLogicType || z.XovisLogicType || '').toUpperCase() !== 'GENERAL');
      setAllDeviceZones(nonGeneral);
    }).catch(() => setAllDeviceZones([]));
  }, [deviceId]);

  useEffect(() => {
    if (!deviceId) {
      setZoneMetrics([]);
      setCommittedZoneMetrics([]);
      return;
    }
    deviceAPI.getZoneMetrics(deviceId, startDate, endDate, granularity)
      .then(res => {
        const zones = Array.isArray(res?.value)
          ? res.value.filter(z => (z.xovisLogicType || '').toUpperCase() !== 'GENERAL')
          : [];
        setZoneMetrics(zones);
        setCommittedZoneMetrics(zones);
        setCommittedPeriod(period);
        setCommittedStartDate(startDate);
        setCommittedEndDate(endDate);
        setCommittedGranularity(granularity);
      })
      .catch(() => {
        setZoneMetrics([]);
        setCommittedZoneMetrics([]);
      });
  }, [deviceId, startDate, endDate, granularity, period]);

  const availableZones = useMemo(() => {
    const list = [];
    const seen = new Set();

    // Helper: Ray-Casting Point-in-Polygon spatial containment
    const isPointInPolygon = (px, py, polygon) => {
      if (!Array.isArray(polygon) || polygon.length < 3) return false;
      let inside = false;
      for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i][0];
        const yi = polygon[i][1];
        const xj = polygon[j][0];
        const yj = polygon[j][1];
        const intersect = ((yi > py) !== (yj > py)) && (px < (((xj - xi) * (py - yi)) / (yj - yi + 1e-9)) + xi);
        if (intersect) inside = !inside;
      }
      return inside;
    };

    const getParentZoneName = (zone, allZones) => {
      try {
        const geoJsonStr = zone.sensorGeometryJson || zone.SensorGeometryJson;
        const pts = typeof geoJsonStr === 'string' ? JSON.parse(geoJsonStr) : geoJsonStr;
        if (!Array.isArray(pts) || pts.length === 0) return null;

        const sumX = pts.reduce((acc, p) => acc + (p[0] || 0), 0);
        const sumY = pts.reduce((acc, p) => acc + (p[1] || 0), 0);
        const midX = sumX / pts.length;
        const midY = sumY / pts.length;

        const areaZones = (allZones || []).filter(z => {
          const zid = z.id || z.zoneId;
          const zidCur = zone.id || zone.zoneId;
          if (String(zid) === String(zidCur)) return false;
          const type = (z.xovisLogicType || z.XovisLogicType || z.type || '').toUpperCase();
          return type === 'GENERAL' || type.startsWith('XLT_ZONE');
        });

        for (const parent of areaZones) {
          const pGeoStr = parent.sensorGeometryJson || parent.SensorGeometryJson;
          const pPts = typeof pGeoStr === 'string' ? JSON.parse(pGeoStr) : pGeoStr;
          if (Array.isArray(pPts) && pPts.length >= 3 && isPointInPolygon(midX, midY, pPts)) {
            return parent.name || parent.zoneName;
          }
        }
      } catch (_) {
        /* ignore geometry parsing errors */
      }
      return null;
    };

    const sourceZones = [
      ...(heatmapData?.stats?.zones && Array.isArray(heatmapData.stats.zones) ? heatmapData.stats.zones : []),
      ...(isCompareActive && heatmapDataB?.stats?.zones && Array.isArray(heatmapDataB.stats.zones) ? heatmapDataB.stats.zones : []),
      ...(zoneMetrics || [])
    ];

    sourceZones
      .filter(z => (z.xovisLogicType || z.XovisLogicType || z.type || '').toUpperCase() !== 'GENERAL')
      .forEach(z => {
        const zid = z.id || z.zoneId;
        if (zid && !seen.has(String(zid).toLowerCase())) {
          seen.add(String(zid).toLowerCase());
          list.push({
            zoneId: zid,
            zoneName: z.name || z.zoneName,
            xovisLogicType: z.xovisLogicType || z.XovisLogicType || '',
            parentZoneName: getParentZoneName(z, allDeviceZones),
            isActive: z.isActive !== false && z.IsActive !== false,
          });
        }
      });

    return list;
  }, [heatmapData, heatmapDataB, isCompareActive, allDeviceZones, zoneMetrics]);

  const isDisplayableLogicZone = useCallback((z) => {
    if (!z) return false;
    const type = (z.xovisLogicType || z.XovisLogicType || z.type || '').toUpperCase().trim();
    if (!type || type === 'GENERAL' || type === 'GEOMETRY') return false;
    const name = (z.name || z.zoneName || '').toLowerCase().trim();
    if (name === 'general' || name === 'geral') return false;
    return true;
  }, []);

  const comparisonZones = useMemo(() => {
    const zonesA = heatmapData?.stats?.zones || [];
    if (!isCompareActive || !heatmapDataB) {
      return zonesA.map((zA, idx) => ({
        key: `zone-A-${zA.id || zA.logicId || zA.name}`,
        name: zA.name,
        badgeNum: zA.badgeNum ?? (idx + 1),
        originalIdx: zA.originalIdx ?? idx,
        xovisLogicType: zA.xovisLogicType,
        zoneA: zA,
        zoneB: zA,
        status: 'BOTH',
      }));
    }

    const zonesB = (heatmapDataB?.stats?.zones || []).filter(isDisplayableLogicZone);
    const map = new Map();
    const normalizeKey = (str) => String(str || '').toLowerCase().trim();

    zonesA.forEach((zA, idx) => {
      const normName = normalizeKey(zA.name);
      const normId = zA.id ? `id:${normalizeKey(zA.id)}` : null;
      const normLogicId = zA.logicId !== null && zA.logicId !== undefined ? `logic:${normalizeKey(zA.logicId)}` : null;

      const entry = {
        key: `zone-${normId || normLogicId || normName}`,
        name: zA.name,
        badgeNum: zA.badgeNum ?? (idx + 1),
        originalIdx: zA.originalIdx ?? idx,
        xovisLogicType: zA.xovisLogicType,
        zoneA: zA,
        zoneB: null,
        status: 'ONLY_A',
      };

      if (normId) map.set(normId, entry);
      if (normLogicId) map.set(normLogicId, entry);
      if (normName) map.set(`name:${normName}`, entry);
    });

    const addedEntries = new Set();
    const result = [];

    zonesA.forEach(zA => {
      const normName = normalizeKey(zA.name);
      const normId = zA.id ? `id:${normalizeKey(zA.id)}` : null;
      const normLogicId = zA.logicId !== null && zA.logicId !== undefined ? `logic:${normalizeKey(zA.logicId)}` : null;
      const entry = (normId && map.get(normId)) || (normLogicId && map.get(normLogicId)) || (normName && map.get(`name:${normName}`));
      if (entry && !addedEntries.has(entry)) {
        addedEntries.add(entry);
        result.push(entry);
      }
    });

    zonesB.forEach((zB, idx) => {
      const normName = normalizeKey(zB.name);
      const normId = zB.id ? `id:${normalizeKey(zB.id)}` : null;
      const normLogicId = zB.logicId !== null && zB.logicId !== undefined ? `logic:${normalizeKey(zB.logicId)}` : null;

      const matchedEntry = (normId && map.get(normId)) ||
                           (normLogicId && map.get(normLogicId)) ||
                           (normName && map.get(`name:${normName}`));

      if (matchedEntry) {
        matchedEntry.zoneB = zB;
        if (!matchedEntry.xovisLogicType) matchedEntry.xovisLogicType = zB.xovisLogicType;
        matchedEntry.status = 'BOTH';
      } else {
        const isKnownDeviceZone = (allDeviceZones || []).some(adz => (
          (adz.zoneId && String(adz.zoneId).toLowerCase() === String(zB.id).toLowerCase()) ||
          (adz.zoneName && adz.zoneName.toLowerCase().trim() === zB.name?.toLowerCase().trim())
        ));
        if (isKnownDeviceZone || (zB.xovisLogicType && zB.xovisLogicType.toUpperCase().startsWith('XLT_'))) {
          const entry = {
            key: `zone-B-${normId || normLogicId || normName}`,
            name: zB.name,
            badgeNum: zB.badgeNum ?? (result.length + 1),
            originalIdx: zB.originalIdx ?? (result.length + idx),
            xovisLogicType: zB.xovisLogicType,
            zoneA: null,
            zoneB: zB,
            status: 'ONLY_B',
          };
          if (!addedEntries.has(entry)) {
            addedEntries.add(entry);
            result.push(entry);
          }
        }
      }
    });

    return result;
  }, [heatmapData, heatmapDataB, isCompareActive, allDeviceZones, isDisplayableLogicZone]);

  useEffect(() => {
    if (availableZones.length > 0) {
      setSelectedZoneIds(cur => {
        if (!Array.isArray(cur) || cur.length === 0) {
          return availableZones.map(z => z.zoneId); // Default: ALL logic zones selected
        }
        const valid = cur.filter(id => availableZones.some(z => String(z.zoneId).toLowerCase() === String(id).toLowerCase()));
        return valid.length > 0 ? valid : availableZones.map(z => z.zoneId);
      });
    } else {
      setSelectedZoneIds([]);
    }
  }, [availableZones]);

  const selectedZoneSeries = useMemo(() => {
    if (!Array.isArray(selectedZoneIds) || selectedZoneIds.length === 0 || !Array.isArray(committedZoneMetrics)) return [];

    const activeZones = committedZoneMetrics.filter(z => selectedZoneIds.some(id => String(id).toLowerCase() === String(z.zoneId).toLowerCase()));

    if (activeZones.length === 0) return [];

    // Aggregate series points across all selected zones by period timestamp
    const map = new Map();

    activeZones.forEach(z => {
      if (Array.isArray(z.series)) {
        z.series.forEach(item => {
          const pKey = item.period;
          if (!map.has(pKey)) {
            map.set(pKey, {
              period: pKey,
              totalInCount: 0,
              totalOutCount: 0,
              avgDwellSeconds: 0,
              _totalDwellWeight: 0,
              totalMaleCount: 0,
              totalFemaleCount: 0,
              totalChildCount: 0,
              totalAdultCount: 0,
              totalElderCount: 0,
              peakOccupancy: 0,
            });
          }
          const agg = map.get(pKey);
          const inCount = item.totalInCount || 0;
          const outCount = item.totalOutCount || 0;
          agg.totalInCount += inCount;
          agg.totalOutCount += outCount;
          agg.totalMaleCount += item.totalMaleCount || 0;
          agg.totalFemaleCount += item.totalFemaleCount || 0;
          agg.totalChildCount += item.totalChildCount || 0;
          agg.totalAdultCount += item.totalAdultCount || 0;
          agg.totalElderCount += item.totalElderCount || 0;
          agg.peakOccupancy = Math.max(agg.peakOccupancy, item.peakOccupancy || 0);

          if (item.avgDwellSeconds > 0) {
            const weight = inCount > 0 ? inCount : (outCount > 0 ? outCount : 1);
            agg.avgDwellSeconds += item.avgDwellSeconds * weight;
            agg._totalDwellWeight += weight;
          }
        });
      }
    });

    const parseMs = str => {
      if (typeof str === 'number') return str;
      if (!str) return 0;
      if (str instanceof Date) return str.getTime();
      const s = String(str);
      const iso = !s.endsWith('Z') && !s.includes('+') && !s.includes('-') ? s.replace(' ', 'T') + 'Z' : s;
      return new Date(iso).getTime();
    };

    const result = Array.from(map.values()).map(agg => ({
      ...agg,
      avgDwellSeconds: agg._totalDwellWeight > 0 ? Math.round(agg.avgDwellSeconds / agg._totalDwellWeight) : 0,
    }));

    result.sort((a, b) => parseMs(a.period) - parseMs(b.period));

    if (committedPeriod === 'today') {
      const nowMs = Date.now();
      return result.filter((agg) => {
        const ms = parseMs(agg.period);
        return ms <= nowMs;
      });
    }

    if (committedStartDate) {
      const startMs = parseMs(committedStartDate);
      const startDayStr = (() => {
        if (typeof committedStartDate === 'string' && committedStartDate.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(committedStartDate)) {
          return committedStartDate.substring(0, 10);
        }
        const d = committedStartDate instanceof Date ? committedStartDate : new Date(committedStartDate);
        if (isNaN(d.getTime())) {
          return '';
        }
        return d.toISOString().substring(0, 10);
      })();

      return result.filter((agg) => {
        const ms = parseMs(agg.period);
        if (ms < startMs) {
          return false;
        }
        if (startDayStr) {
          const rowDayStr = String(agg.period || '').substring(0, 10);
          if (rowDayStr && rowDayStr < startDayStr) {
            return false;
          }
        }
        return true;
      });
    }

    return result;
  }, [committedZoneMetrics, selectedZoneIds, committedPeriod, committedStartDate]);

  const [allStores, setAllStores] = useState([]);

  const handleCustomStartDate = useCallback((date) => {
    setPeriod('custom');
    if (!date) {
      return;
    }
    setStartDate(toUtcStartOfDay(date));
  }, []);

  const handleCustomEndDate = useCallback((date) => {
    setPeriod('custom');
    if (!date) {
      return;
    }
    setEndDate(toUtcEndOfDay(date));
  }, []);

  const handleCustomStartDateB = useCallback((date) => {
    setPeriodB('custom');
    if (!date) {
      return;
    }
    setStartDateB(toUtcStartOfDay(date));
  }, []);

  const handleCustomEndDateB = useCallback((date) => {
    setPeriodB('custom');
    if (!date) {
      return;
    }
    setEndDateB(toUtcEndOfDay(date));
  }, []);

  const handleToggleCompare = useCallback(() => {
    setIsCompareActive(prev => {
      const next = !prev;
      if (next) {
        // Move current selection to Period B
        setPeriodB(period);
        setStartDateB(startDate);
        setEndDateB(endDate);

        // Compute Period B's duration and assign Period A to the preceding equal-length window
        const curStart = startDate ? new Date(startDate) : new Date();
        const curEnd = endDate ? new Date(endDate) : new Date();

        const diffMs = Math.max(0, curEnd.getTime() - curStart.getTime());
        const diffDays = Math.max(1, Math.round(diffMs / (24 * 60 * 60 * 1000)));

        // Period A end date = day before Period B start date
        const yEnd = new Date(curStart.getTime() - 86400000);
        // Period A start date = same duration preceding yEnd
        const yStart = new Date(yEnd.getTime() - (diffDays - 1) * 86400000);

        setPeriod('custom');
        setStartDate(toUtcStartOfDay(yStart));
        setEndDate(toUtcEndOfDay(yEnd));
      } else {
        // Exit comparison: restore Period B's filter back to the main temporal filter
        setPeriod(periodB);
        setStartDate(startDateB);
        setEndDate(endDateB);
      }
      return next;
    });
  }, [period, startDate, endDate, periodB, startDateB, endDateB]);

  useEffect(() => {
    if (!selectedClient?.id) {
      setAllStores([]);
      setStoreId('');
      setDeviceId('');
      setHeatmapData(null);
      return;
    }

    storeAPI.getByClient(selectedClient.id).then(res => {
      setAllStores(Array.isArray(res?.value) ? res.value : []);
      setStoreId('');
      setDeviceId('');
      setHeatmapData(null);
    }).catch(err => {
      console.error(err);
      setAllStores([]);
      setStoreId('');
      setDeviceId('');
      setHeatmapData(null);
    });
  }, [selectedClient?.id]);

  const periodLabel = useMemo(() => {
    return formatTemporalWindow(startDate, endDate, t);
  }, [startDate, endDate, t]);

  const selectedStore = allStores.find(s => s.id === storeId) || null;
  const [devices, setDevices] = useState([]);
  const selectedDevice = devices.find(d => d.id === deviceId) || null;

  const applyDefaultFilterForDevice = useCallback((dev) => {
    if (!dev) {
      return;
    }

    if (dev.isCurrent === false) {
      // Historical device: default temporal filter is its most recent time stint
      const mostRecentStint = (dev.stints && dev.stints.length > 0) ? dev.stints[0] : null;
      const stintStart = mostRecentStint?.associatedAt
        ? new Date(mostRecentStint.associatedAt)
        : (dev.associatedAt ? new Date(dev.associatedAt) : null);
      const stintEnd = mostRecentStint?.disassociatedAt
        ? new Date(mostRecentStint.disassociatedAt)
        : (dev.disassociatedAt ? new Date(dev.disassociatedAt) : new Date());

      setPeriod('custom');
      if (stintStart) {
        setStartDate(stintStart);
      }
      if (stintEnd) {
        setEndDate(stintEnd);
      }
    } else {
      // Active / non-historical device: default temporal filter is today
      setPeriod('today');
      setStartDate(toUtcStartOfDay(new Date()));
      setEndDate(new Date());
    }
  }, []);

  useEffect(() => {
    if (!storeId) {
      setDevices([]);
      return;
    }
    Promise.allSettled([
      deviceAPI.getByStore(storeId),
      deviceAPI.getStoreHistory(storeId)
    ]).then(([byStoreRes, historyRes]) => {
      const activeList = (byStoreRes.status === 'fulfilled' && Array.isArray(byStoreRes.value?.value))
        ? byStoreRes.value.value
        : [];
      const historyList = (historyRes.status === 'fulfilled' && Array.isArray(historyRes.value?.value))
        ? historyRes.value.value
        : [];

      const deviceMap = new Map();

      // First add active devices
      activeList.forEach(d => {
        if (!deviceMap.has(d.id)) {
          deviceMap.set(d.id, {
            id: d.id,
            name: d.name,
            serial: d.serialNumber,
            location: d.location,
            hasAutoCadImage: d.hasAutoCadImage,
            isCurrent: true,
          });
        }
      });

      // Then add/merge historical devices
      historyList.forEach(item => {
        const stint = {
          id: item.id,
          associatedAt: item.associatedAt,
          disassociatedAt: item.disassociatedAt,
          isCurrent: item.isCurrent,
        };

        if (!deviceMap.has(item.deviceId)) {
          deviceMap.set(item.deviceId, {
            id: item.deviceId,
            name: item.deviceName,
            serial: item.serialNumber,
            location: item.location,
            isCurrent: item.isCurrent,
            associatedAt: item.associatedAt,
            disassociatedAt: item.disassociatedAt,
            stints: [stint],
          });
        } else {
          const existing = deviceMap.get(item.deviceId);
          if (existing) {
            if (item.isCurrent) {
              existing.isCurrent = true;
            }
            existing.stints = existing.stints || [];
            const alreadyIn = existing.stints.some(s => s.id === stint.id || (s.associatedAt === stint.associatedAt && s.disassociatedAt === stint.disassociatedAt));
            if (!alreadyIn) {
              existing.stints.push(stint);
            }
            if (!existing.associatedAt) {
              existing.associatedAt = item.associatedAt;
              existing.disassociatedAt = item.disassociatedAt;
            }
          }
        }
      });

      const mapped = Array.from(deviceMap.values()).sort((a, b) => {
        const aCurrent = a.isCurrent !== false;
        const bCurrent = b.isCurrent !== false;
        if (aCurrent && !bCurrent) {
          return -1;
        }
        if (!aCurrent && bCurrent) {
          return 1;
        }
        return (a.name || a.serial || '').localeCompare(b.name || b.serial || '');
      });

      setDevices(mapped);
      if (mapped.length === 1) {
        setDeviceId(mapped[0].id);
        applyDefaultFilterForDevice(mapped[0]);
      }
    }).catch(err => {
      console.error(err);
      setDevices([]);
    });
  }, [storeId, applyDefaultFilterForDevice]);

  const loadHeatmap = useCallback((devId, startDate, endDate, targetStoreId) => {
    setIsLoading(true);
    Promise.all([
      heatmapAPI.getTemporalSummary(devId, startDate, endDate, targetStoreId),
      heatmapAPI.getRecentByDevice(devId, 1),
      deviceAPI.getZones(devId)
    ])
      .then(([summaryRes, heatmapRes, zonesRes]) => {
        const summary = summaryRes?.value || summaryRes;
        const heatmap = heatmapRes?.value?.[0];

        const rawZones = Array.isArray(zonesRes?.value) ? zonesRes.value : [];
        setAllDeviceZones(rawZones.filter(z => (z.xovisLogicType || z.XovisLogicType || '').toUpperCase() !== 'GENERAL'));

        // Apply historical zone filter (keeps currently inactive zones if active during [start, end])
        const historicalRawZones = filterHistoricalZones(
          summary?.historicalZones && summary.historicalZones.length > 0 ? summary.historicalZones : rawZones,
          startDate,
          endDate
        );

        const rawDataPoints = summary ? summary.netDataPoints : (heatmap?.dataPoints || '[]');

        if (rawDataPoints === null || rawDataPoints === undefined) {
          setHeatmapData(null);
          setIsLoading(false);
          return;
        }

        const hotspots = [];
        let dataPoints = [];
        try {
          dataPoints = typeof rawDataPoints === 'string'
            ? JSON.parse(rawDataPoints)
            : rawDataPoints;
        } catch (e) {
          console.error('Failed to parse heatmap DataPoints', e);
        }

        const maxVal = summary?.densityMax || heatmap?.densityMax || 1;
        const minVal = summary?.densityMin || heatmap?.densityMin || 0;
        const width = summary?.width || summary?.gridWidth || heatmap?.width || 45;
        const height = summary?.height || summary?.gridHeight || heatmap?.height || 25;

        let metadata = null;
        try {
          const metaJson = summary?.heatmapMetadataJson || heatmap?.heatmapMetadataJson;
          metadata = metaJson ? JSON.parse(metaJson) : null;
        } catch (e) {
          console.error('Failed to parse heatmap metadata', e);
        }

        if (Array.isArray(dataPoints)) {
          for (let y = 0; y < dataPoints.length; y++) {
            const row = dataPoints[y];
            if (Array.isArray(row)) {
              for (let x = 0; x < row.length; x++) {
                const val = row[x];
                if (val > 0) {
                  const rawIntensity = maxVal > minVal ? (val - minVal) / (maxVal - minVal) : val;
                  // Filter out low-density noise to prevent background artifacts
                  if (rawIntensity >= NOISE_THRESHOLD) {
                    // Apply non-linear power curve and multiplier for visual tuning
                    const intensity = Math.min(1.0, (rawIntensity ** INTENSITY_BOOST_EXPONENT) * INTENSITY_MULTIPLIER);
                    hotspots.push({
                      cx: (x + 0.5) / width,
                      cy: (y + 0.5) / height,
                      r: BLENDING_RADIUS / width, // optimal radius for blending
                      intensity,
                    });
                  }
                }
              }
            }
          }
        }

        // Process real zones using metadata transformation
        let realZones = [];
        if (metadata && metadata.ref_to_pixel && Array.isArray(historicalRawZones)) {
          const { linear, translation } = metadata.ref_to_pixel;
          const metaW = metadata.width_px || width;
          const metaH = metadata.height_px || height;
          if (linear && translation) {
            realZones = historicalRawZones
              .map(z => {
              let vertices = [];
              try {
                if (z.sensorGeometryJson) {
                  const rawCoords = JSON.parse(z.sensorGeometryJson);
                  if (Array.isArray(rawCoords)) {
                    vertices = rawCoords.map(([cxVal, cyVal]) => {
                      const px = linear[0][0] * cxVal + linear[0][1] * cyVal + translation[0];
                      const py = linear[1][0] * cxVal + linear[1][1] * cyVal + translation[1];
                      return {
                        nx: px / metaW,
                        ny: (metaH - py) / metaH,
                      };
                    });
                  }
                }
              } catch (e) {
                console.error('Error parsing zone coordinates', e);
              }
              return {
                id: z.id,
                name: z.name,
                geometryType: z.geometryType,
                vertices,
                occupancy: z.occupancy,
                inCount: z.inCount,
                outCount: z.outCount,
                cumulativeVisits: z.cumulativeVisits,
                xovisLogicType: z.xovisLogicType,
                avgDwellSeconds: z.avgDwellSeconds,
                maleCount: z.maleCount,
                femaleCount: z.femaleCount,
                unclassifiedCount: z.unclassifiedCount,
                ageBin0: z.ageBin0,
                ageBin1: z.ageBin1,
                ageBin2: z.ageBin2,
                ageBin3: z.ageBin3,
                ageThresholdsJson: z.ageThresholdsJson,
              };
            });
          }
        }

        let statsZones = [];
        if (realZones.length > 0) {
          let currentBadgeNum = 1;
          const logicCountersMap = new Map();
          if (Array.isArray(summary?.logicCounters)) {
            summary.logicCounters.forEach(lc => {
              if (lc.zoneId) logicCountersMap.set(String(lc.zoneId).toLowerCase(), lc);
              if (lc.xovisLogicId !== null && lc.xovisLogicId !== undefined) {
                logicCountersMap.set(String(lc.xovisLogicId), lc);
              }
              if (lc.zoneName) logicCountersMap.set(lc.zoneName.toLowerCase().trim(), lc);
            });
          }

          statsZones = realZones
            .map((z, idx) => ({ ...z, originalIdx: idx }))
            .filter(z => (z.xovisLogicType || z.type || '').toUpperCase() !== 'GENERAL')
            .map(z => {
              const lc = (z.id && logicCountersMap.get(String(z.id).toLowerCase()))
                || (z.xovisLogicId !== null && z.xovisLogicId !== undefined && logicCountersMap.get(String(z.xovisLogicId)))
                || (z.name && logicCountersMap.get(z.name.toLowerCase().trim()))
                || {};

              const inCount = lc.totalIn ?? z.inCount ?? 0;
              const outCount = lc.totalOut ?? z.outCount ?? 0;
              const occupancy = lc.occupancy ?? z.occupancy ?? 0;
              const avgDwellSeconds = lc.avgDwellTimeSeconds ?? z.avgDwellSeconds ?? 0;
              const cumulativeVisits = lc.cumulativeVisits ?? z.cumulativeVisits ?? (inCount > 0 ? inCount : outCount);

              const hasTemporalDemographics = (lc.maleCount !== undefined && lc.maleCount !== null) || (lc.femaleCount !== undefined && lc.femaleCount !== null) || (lc.childCount !== undefined && lc.childCount !== null);
              const isDemographicCumulative = !hasTemporalDemographics && ((z.maleCount ?? 0) > 0 || (z.femaleCount ?? 0) > 0 || (z.ageBin0 ?? 0) > 0);

              return {
                id: z.id,
                name: z.name,
                count: occupancy > 0 ? occupancy : inCount,
                occupancy,
                inCount,
                outCount,
                cumulativeVisits,
                xovisLogicType: z.xovisLogicType,
                avgDwellSeconds,
                maleCount: (lc.maleCount !== undefined && lc.maleCount !== null) ? lc.maleCount : (z.maleCount ?? 0),
                femaleCount: (lc.femaleCount !== undefined && lc.femaleCount !== null) ? lc.femaleCount : (z.femaleCount ?? 0),
                childCount: (lc.childCount !== undefined && lc.childCount !== null) ? lc.childCount : (z.childCount ?? z.ageBin0 ?? 0),
                adultCount: (lc.adultCount !== undefined && lc.adultCount !== null) ? lc.adultCount : (z.adultCount ?? z.ageBin1 ?? 0),
                elderCount: (lc.elderCount !== undefined && lc.elderCount !== null) ? lc.elderCount : (z.elderCount ?? z.ageBin2 ?? 0),
                ageBin0: (lc.childCount !== undefined && lc.childCount !== null) ? lc.childCount : (z.childCount ?? z.ageBin0 ?? 0),
                ageBin1: (lc.adultCount !== undefined && lc.adultCount !== null) ? lc.adultCount : (z.adultCount ?? z.ageBin1 ?? 0),
                ageBin2: (lc.elderCount !== undefined && lc.elderCount !== null) ? lc.elderCount : (z.elderCount ?? z.ageBin2 ?? 0),
                ageBin3: z.ageBin3 ?? 0,
                ageThresholdsJson: z.ageThresholdsJson || '[16, 40, 65]',
                isDemographicCumulative,
                originalIdx: z.originalIdx,
                badgeNum: currentBadgeNum++
              };
            });
        }

        const totalDetections = Array.isArray(summary?.logicCounters) && summary.logicCounters.length > 0
          ? summary.logicCounters.reduce((acc, lc) => acc + (lc.totalIn || 0), 0)
          : (statsZones.length > 0 ? statsZones.reduce((acc, curr) => acc + (curr.inCount || curr.cumulativeVisits || 0), 0) : 0);
        const mostVisitedObj = statsZones.length > 0
          ? statsZones.reduce((a, b) => (a.count >= b.count ? a : b), statsZones[0])
          : null;
        const mostVisited = mostVisitedObj ? mostVisitedObj.name : '—';
        const stats = { totalDetections, zones: statsZones, mostVisited };

        setHeatmapData({
          hotspots,
          realZones,
          stats,
          gridWidth: width,
          gridHeight: height,
        });
        setIsLoading(false);
      })
      .catch(err => {
        console.error(err);
        setHeatmapData(null);
        setIsLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!deviceId) { setHeatmapData(null); return; }
    loadHeatmap(deviceId, startDate, endDate, storeId);
  }, [deviceId, storeId, startDate, endDate, loadHeatmap]);

  useEffect(() => {
    if (!deviceId || !isCompareActive) {
      setHeatmapDataB(null);
      return;
    }
    setIsLoadingB(true);
    heatmapAPI.getTemporalSummary(deviceId, startDateB, endDateB, storeId)
      .then(summaryRes => {
        const summary = summaryRes?.value || summaryRes;
        const rawDevZones = (allDeviceZones && allDeviceZones.length > 0)
          ? allDeviceZones
          : (heatmapData?.realZones || []);

        const logicCountersMap = new Map();
        if (Array.isArray(summary?.logicCounters)) {
          summary.logicCounters.forEach(lc => {
            if (lc.zoneId) logicCountersMap.set(String(lc.zoneId).toLowerCase(), lc);
            if (lc.xovisLogicId !== null && lc.xovisLogicId !== undefined) {
              logicCountersMap.set(String(lc.xovisLogicId), lc);
            }
            if (lc.zoneName) logicCountersMap.set(lc.zoneName.toLowerCase().trim(), lc);
          });
        }

        let currentBadgeNum = 1;
        const statsZones = rawDevZones
          .filter(z => {
            const type = (z.xovisLogicType || z.XovisLogicType || z.type || '').toUpperCase().trim();
            const name = (z.name || z.zoneName || '').toLowerCase().trim();
            return type && type !== 'GENERAL' && type !== 'GEOMETRY' && name !== 'general' && name !== 'geral';
          })
          .map((z, idx) => {
            const lc = (z.id && logicCountersMap.get(String(z.id).toLowerCase()))
              || (z.zoneId && logicCountersMap.get(String(z.zoneId).toLowerCase()))
              || (z.xovisLogicId !== null && z.xovisLogicId !== undefined && logicCountersMap.get(String(z.xovisLogicId)))
              || (z.name && logicCountersMap.get(z.name.toLowerCase().trim()))
              || {};

            const inCount = lc.totalIn ?? z.inCount ?? 0;
            const outCount = lc.totalOut ?? z.outCount ?? 0;
            const occupancy = lc.occupancy ?? z.occupancy ?? 0;
            const avgDwellSeconds = lc.avgDwellTimeSeconds ?? z.avgDwellSeconds ?? 0;
            const cumulativeVisits = lc.cumulativeVisits ?? z.cumulativeVisits ?? (inCount > 0 ? inCount : outCount);

            return {
              id: z.id || z.zoneId,
              name: z.name || z.zoneName,
              logicId: z.xovisLogicId ?? z.logicId,
              xovisLogicType: z.xovisLogicType || z.XovisLogicType || z.type || 'XLT_ZONE',
              count: occupancy > 0 ? occupancy : (inCount > 0 ? inCount : cumulativeVisits),
              occupancy,
              inCount,
              outCount,
              cumulativeVisits,
              avgDwellSeconds,
              maleCount: (lc.maleCount !== undefined && lc.maleCount !== null) ? lc.maleCount : (z.maleCount ?? 0),
              femaleCount: (lc.femaleCount !== undefined && lc.femaleCount !== null) ? lc.femaleCount : (z.femaleCount ?? 0),
              childCount: (lc.childCount !== undefined && lc.childCount !== null) ? lc.childCount : (z.childCount ?? z.ageBin0 ?? 0),
              adultCount: (lc.adultCount !== undefined && lc.adultCount !== null) ? lc.adultCount : (z.adultCount ?? z.ageBin1 ?? 0),
              elderCount: (lc.elderCount !== undefined && lc.elderCount !== null) ? lc.elderCount : (z.elderCount ?? z.ageBin2 ?? 0),
              ageBin0: (lc.childCount !== undefined && lc.childCount !== null) ? lc.childCount : (z.childCount ?? z.ageBin0 ?? 0),
              ageBin1: (lc.adultCount !== undefined && lc.adultCount !== null) ? lc.adultCount : (z.adultCount ?? z.ageBin1 ?? 0),
              ageBin2: (lc.elderCount !== undefined && lc.elderCount !== null) ? lc.elderCount : (z.elderCount ?? z.ageBin2 ?? 0),
              ageBin3: z.ageBin3 ?? 0,
              ageThresholdsJson: z.ageThresholdsJson || '[16, 40, 65]',
              badgeNum: currentBadgeNum++,
              originalIdx: idx,
            };
          });

        const totalDetections = summary?.logicCounters?.reduce((acc, lc) => acc + (lc.totalIn || 0), 0)
          || statsZones.reduce((acc, z) => acc + z.count, 0);

        setHeatmapDataB({
          stats: { totalDetections, zones: statsZones }
        });
        setIsLoadingB(false);
      })
      .catch(err => {
        console.error('[HeatmapViewerPage] Error loading comparison period B:', err);
        setHeatmapDataB(null);
        setIsLoadingB(false);
      });
  }, [deviceId, isCompareActive, startDateB, endDateB, allDeviceZones, heatmapData]);

  // Load device background image for the heatmap canvas
  useEffect(() => {
    if (!deviceId) {
      setBgImage(null);
      return;
    }
    let active = true;
    let objectUrl = null;
    deviceAPI.getLiveImageBlob(deviceId, storeId)
      .then(blob => {
        if (!active) {
          return;
        }
        objectUrl = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => {
          if (active) {
            setBgImage(img);
          }
        };
        img.onerror = () => {
          if (active) {
            setBgImage(null);
          }
        };
        img.src = objectUrl;
      })
      .catch(() => {
        if (active) {
          setBgImage(null);
        }
      });

    return () => {
      active = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [deviceId, storeId, bgImageVersion]);

  const handleStoreChange = e => {
    setStoreId(e.target.value);
    setDeviceId('');
    setBgImage(null);
    setHeatmapData(null);
    setZoneMetrics([]);
    setSelectedZoneIds([]);
    setPeriod('today');
  };

  const handleDeviceChange = e => {
    setBgImage(null);
    const newDevId = e.target.value;
    setDeviceId(newDevId);

    const dev = devices.find(d => d.id === newDevId);
    applyDefaultFilterForDevice(dev);
  };

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 3 }}>
      {isLoading && (
        <LinearProgress sx={{ position: 'absolute', top: 64, left: 0, width: '100%', zIndex: 10 }} />
      )}

      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
      />

      {/* ── Control bar (Store & Device Dropdowns + Dashboard Temporal Filter) ── */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1.5}
        sx={{ mb: 3 }}
      >
        {/* Left: Store & Device Dropdowns + Ajustar Planta Button */}
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.5}>
          {/* Store Dropdown */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
              value={storeId}
              onChange={handleStoreChange}
              displayEmpty
              sx={{ fontSize: '0.8125rem' }}
            >
              <MenuItem value=""><em>{t('filters.selectStore')}</em></MenuItem>
              {allStores.map(s => (
                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Device Dropdown */}
          <FormControl size="small" sx={{ minWidth: 200 }} disabled={!storeId}>
            <Select
              value={deviceId}
              onChange={handleDeviceChange}
              displayEmpty
              sx={{ fontSize: '0.8125rem' }}
            >
              <MenuItem value=""><em>{t('filters.selectDevice')}</em></MenuItem>
              {devices.map((d, index) => {
                const label = d.name
                  ? (d.location ? `${d.name} (${d.location})` : d.name)
                  : (d.location ? `${d.location} · ${d.serial}` : d.serial);
                const showDivider = index > 0 && devices[index - 1].isCurrent !== false && d.isCurrent === false;
                return [
                  showDivider && <Divider key={`divider-${d.id}`} sx={{ my: 0.5 }} />,
                  <MenuItem key={d.id} value={d.id}>
                    {label}{!d.isCurrent ? ` (${t('filters.historical', 'Histórico')})` : ''}
                  </MenuItem>
                ];
              })}
            </Select>
          </FormControl>

          {/* Stint Badge / Multiple Stints Selector (Historical & Active Devices) */}
          {deviceId && selectedDevice && (selectedDevice.isCurrent === false || (selectedDevice.stints && selectedDevice.stints.length > 0) || Boolean(selectedDevice.associatedAt)) && (() => {
            const isHistorical = selectedDevice.isCurrent === false;
            const stints = selectedDevice.stints || [];
            const hasMultipleStints = stints.length > 1;
            const singleStint = stints[0] || {
              associatedAt: selectedDevice.associatedAt,
              disassociatedAt: selectedDevice.disassociatedAt,
            };

            const formatStintEnd = (date) => {
              if (date) {
                return fmtDate(date);
              }
              return isHistorical ? '—' : t('filters.present', 'Presente');
            };

            const stintDescription = isHistorical
              ? (hasMultipleStints
                  ? `${t('filters.historical', 'Histórico')} (${stints.length}): ${fmtDate(startDate || selectedDevice.associatedAt)} – ${endDate ? fmtDate(endDate) : formatStintEnd(selectedDevice.disassociatedAt)}`
                  : `${t('filters.historical', 'Histórico')}: ${fmtDate(selectedDevice.associatedAt)} – ${formatStintEnd(selectedDevice.disassociatedAt)}`)
              : (hasMultipleStints
                  ? `${t('filters.periods', 'Períodos')} (${stints.length}): ${fmtDate(startDate || selectedDevice.associatedAt)} – ${endDate ? fmtDate(endDate) : formatStintEnd(selectedDevice.disassociatedAt)}`
                  : `${t('filters.active', 'Ativa')}: ${fmtDate(singleStint.associatedAt || selectedDevice.associatedAt)} – ${formatStintEnd(singleStint.disassociatedAt || selectedDevice.disassociatedAt)}`);

            const actionDescription = hasMultipleStints
              ? t('filters.stintTooltip', 'Clique para selecionar um período desta câmara nesta loja')
              : (isHistorical
                  ? t('filters.applyStintFilter', 'Clique para filtrar por este período histórico')
                  : t('filters.applyStintFilterActive', 'Clique para filtrar pelo período desta câmara nesta loja'));

            const chipTooltip = `${stintDescription} · ${actionDescription}`;
            const chipColor = isHistorical ? 'warning' : 'info';

            return (
              <>
                <Tooltip title={chipTooltip}>
                  <IconButton
                    size="small"
                    color={chipColor}
                    onClick={(e) => {
                      if (hasMultipleStints) {
                        setStintMenuAnchor(e.currentTarget);
                      } else {
                        const stint = singleStint;
                        setPeriod('custom');
                        if (stint.associatedAt) {
                          setStartDate(new Date(stint.associatedAt));
                        }
                        if (stint.disassociatedAt) {
                          setEndDate(new Date(stint.disassociatedAt));
                        } else {
                          setEndDate(new Date());
                        }
                      }
                    }}
                    sx={{
                      height: 38,
                      width: hasMultipleStints ? 46 : 38,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: isHistorical ? 'warning.main' : 'info.main',
                      color: isHistorical ? 'warning.main' : 'info.main',
                      bgcolor: isHistorical ? 'warning.lighter' : 'info.lighter',
                      p: 0,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0,
                      '&:hover': {
                        bgcolor: isHistorical ? 'warning.light' : 'info.light',
                      },
                    }}
                  >
                    <HistoryIcon fontSize="small" />
                    {hasMultipleStints && (
                      <ArrowDropDownIcon sx={{ fontSize: 18, ml: -0.5 }} />
                    )}
                  </IconButton>
                </Tooltip>

                {hasMultipleStints && (
                  <Menu
                    anchorEl={stintMenuAnchor}
                    open={Boolean(stintMenuAnchor)}
                    onClose={() => { setStintMenuAnchor(null); }}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  >
                    <MenuItem
                      onClick={() => {
                        setPeriod('allTime');
                        setStartDate(null);
                        setEndDate(null);
                        setStintMenuAnchor(null);
                      }}
                      selected={period === 'allTime'}
                    >
                      <ListItemText
                        primary={t('filters.viewAllPeriods', 'Ver todos os períodos')}
                        secondary={t('actions.allTime', 'Desde sempre')}
                      />
                    </MenuItem>
                    <Divider />
                    {stints.map((stint, idx) => {
                      const isSelected = period === 'custom'
                        && startDate && stint.associatedAt && new Date(startDate).getTime() === new Date(stint.associatedAt).getTime();
                      return (
                        <MenuItem
                          key={stint.id || idx}
                          selected={isSelected}
                          onClick={() => {
                            setPeriod('custom');
                            if (stint.associatedAt) {
                              setStartDate(new Date(stint.associatedAt));
                            }
                            if (stint.disassociatedAt) {
                              setEndDate(new Date(stint.disassociatedAt));
                            } else {
                              setEndDate(new Date());
                            }
                            setStintMenuAnchor(null);
                          }}
                        >
                          <ListItemText
                            primary={`${t('filters.stintNumber', { number: idx + 1 })}: ${fmtDate(stint.associatedAt)} – ${formatStintEnd(stint.disassociatedAt)}`}
                            secondary={!stint.disassociatedAt ? t('filters.activeStint', 'Atual / Ativa') : (idx === 0 ? t('filters.mostRecent', 'Mais recente') : undefined)}
                          />
                        </MenuItem>
                      );
                    })}
                  </Menu>
                )}
              </>
            );
          })()}

          {/* Ajustar / Adicionar Planta Button */}
          {canResetHeatmap && deviceId && selectedDevice?.isCurrent !== false && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setAutoCadOpen(true)}
              startIcon={<UploadIcon />}
              sx={{ fontSize: '0.8125rem', textTransform: 'none', height: 40 }}
            >
              {selectedDevice?.hasAutoCadImage ? t('autocad.adjustPlan') : t('autocad.addPlan')}
            </Button>
          )}

          {/* Quick jump to Zone Traffic Chart */}
          {deviceId && (
            <Tooltip title={t('stats.zoneTrafficOverTime', 'Tráfego por Zona ao Longo do Tempo')}>
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={() => zoneChartRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                sx={{
                  minWidth: 40,
                  width: 40,
                  height: 40,
                  p: 0,
                  fontSize: '0.8125rem',
                }}
              >
                <BarChartIcon fontSize="small" />
              </Button>
            </Tooltip>
          )}
        </Stack>

        {/* Right: Temporal Filters */}
        <Stack direction="column" alignItems="flex-start" gap={1.25}>
          {/* Row 1: Comparison Toggle Button + Period A Temporal Filter */}
          <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.5}>
            <Button
              variant={isCompareActive ? "contained" : "outlined"}
              color={isCompareActive ? "secondary" : "primary"}
              size="small"
              onClick={handleToggleCompare}
              sx={{ textTransform: 'none', fontSize: '0.8125rem', height: 32, fontWeight: 600, minWidth: 165 }}
            >
              {isCompareActive ? t('period.cancelCompare', 'Sair da Comparação') : t('period.comparePeriods', 'Comparar 2 Períodos')}
            </Button>

            {isCompareActive && (
              <Chip
                label={t('period.periodA', 'Período A')}
                size="small"
                color="primary"
                sx={{ height: 24, fontSize: '0.75rem', fontWeight: 700, minWidth: 80 }}
              />
            )}

            <PeriodToggle
              value={period}
              onChange={setPeriod}
              t={t}
            />
            <DateRangePopover
              startDate={startDate}
              endDate={endDate}
              onStartChange={handleCustomStartDate}
              onEndChange={handleCustomEndDate}
              startLabel={t('period.dateStart', 'Data Inicial')}
              endLabel={t('period.dateEnd', 'Data Final')}
            />
          </Stack>

          {/* Row 2: Period B Temporal Filter (Appears directly below Row 1, aligned with Period A) */}
          {isCompareActive && (
            <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.5}>
              <Box sx={{ minWidth: 165, display: { xs: 'none', sm: 'block' } }} />
              <Chip
                label={t('period.periodB', 'Período B')}
                size="small"
                variant="outlined"
                sx={{ height: 24, fontSize: '0.75rem', fontWeight: 700, minWidth: 80, borderColor: 'primary.dark', color: 'primary.dark' }}
              />
              <PeriodToggle
                value={periodB}
                onChange={setPeriodB}
                t={t}
                flipped
              />
              <DateRangePopover
                startDate={startDateB}
                endDate={endDateB}
                onStartChange={handleCustomStartDateB}
                onEndChange={handleCustomEndDateB}
                startLabel={t('period.dateStartB', 'Data Inicial B')}
                endLabel={t('period.dateEndB', 'Data Final B')}
              />
            </Stack>
          )}
        </Stack>
      </Stack>

      {/* ── Main content ── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 300px' },
          gap: 2,
          alignItems: 'start',
        }}
      >
        {/* ── Left: Heatmap canvas card ── */}
        <Card sx={{ height: 'fit-content', alignSelf: 'start' }}>
          <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
            {/* Card header row: store label left, toggles right */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={1}
              sx={{ mb: 2 }}
            >
              <Stack
                direction="row"
                alignItems="center"
                gap={1.5}
              >
                {selectedDevice ? (
                  <>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600 }}
                    >
                      {selectedStore ? selectedStore.name : ''}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                    >
                      · {selectedDevice.serial}
                    </Typography>
                    {!selectedDevice.isCurrent && (
                      <Chip
                        label={t('filters.historical', 'Histórico')}
                        size="small"
                        color="default"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                      />
                    )}
                  </>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    {t('canvas.noDevice')}
                  </Typography>
                )}
              </Stack>

              <Stack
                direction="row"
                alignItems="center"
                gap={1}
              >
                <ToggleButtonGroup
                  value={viewType}
                  exclusive
                  onChange={(_, v) => { if (v) setViewType(v); }}
                  size="small"
                  sx={TOGGLE_SX}
                >
                  <ToggleButton value="densidade">{t('viewType.density')}</ToggleButton>
                  <ToggleButton value="trajectos" sx={{ display: 'none' }}>{t('viewType.trajectories')}</ToggleButton>
                </ToggleButtonGroup>

                {heatmapData && (
                  <Tooltip title={t('canvas.fullscreen', 'Ecrã Inteiro')}>
                    <IconButton
                      size="small"
                      onClick={() => setIsFullscreen(true)}
                      sx={{
                        color: 'text.secondary',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 0.5,
                        '&:hover': { bgcolor: 'action.hover', color: 'primary.main' }
                      }}
                    >
                      <FullscreenIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Stack>

            {/* Canvas or placeholder */}
            {heatmapData && !isLoading ? (
              <HeatmapCanvas
                hotspots={heatmapData.hotspots}
                realZones={heatmapData.realZones}
                bgImage={bgImage}
                gridWidth={heatmapData.gridWidth}
                gridHeight={heatmapData.gridHeight}
                hoveredZoneIdx={hoveredZoneIdx}
                onZoneHover={(idx) => {
                  setHoveredZoneIdx(idx);
                }}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  maxWidth: { xs: '100%', lg: CANVAS_MAX_W_DEFAULT, xl: CANVAS_MAX_W_XL },
                  aspectRatio: `${CANVAS_W}/${CANVAS_H}`,
                  bgcolor: '#0B1D35',
                  borderRadius: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1.5,
                  ml: 0,
                  mr: 'auto',
                }}
              >
                {(isLoading || isLoadingB) ? (
                  <CircularProgress
                    size={36}
                    sx={{ color: 'rgba(255,255,255,0.45)' }}
                  />
                ) : deviceId ? (
                  <>
                    <WarningIcon sx={{ fontSize: 52, color: 'rgba(255,255,255,0.32)' }} />
                    <Typography
                      variant="body2"
                      sx={{ color: 'rgba(255,255,255,0.50)', textAlign: 'center', px: 3 }}
                    >
                      {t('canvas.noData')}
                    </Typography>
                  </>
                ) : (
                  <>
                    <BlurOnIcon sx={{ fontSize: 52, color: 'rgba(255,255,255,0.18)' }} />
                    <Typography
                      variant="body2"
                      sx={{ color: 'rgba(255,255,255,0.32)', textAlign: 'center', px: 3 }}
                    >
                      {t('canvas.placeholder')}
                    </Typography>
                  </>
                )}
              </Box>
            )}
          </CardContent>
        </Card>

        {/* ── Right: Statistics panel ── */}
        <Card sx={{ height: 'fit-content', alignSelf: 'start' }}>
          <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 2 }}
            >
              <Typography
variant="subtitle2"
sx={{ fontWeight: 600 }}
              >
                {t('stats.periodSummary')}
              </Typography>
              <Typography
variant="caption"
color="text.secondary"
              >
                {periodLabel}
              </Typography>
            </Stack>

            {heatmapData ? (
              <>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    textTransform: 'uppercase',
                    fontSize: 10,
                    letterSpacing: 0.9,
                    color: 'text.secondary',
                  }}
                >
                  {t('stats.totalDetections')}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{ fontWeight: 700, mt: 0.5, mb: 0.5, lineHeight: 1.2 }}
                >
                  {fmtNum(heatmapData.stats.totalDetections)}
                </Typography>

                {isCompareActive && heatmapDataB && (
                  <Box sx={{ p: 1.25, mt: 1, mb: 1, borderRadius: 1, bgcolor: 'rgba(156,39,176,0.08)', border: '1px solid rgba(156,39,176,0.3)' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#ab47bc', display: 'block', mb: 0.5 }}>
                      {t('period.periodComparison', 'Comparação de Períodos')}
                    </Typography>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="caption" display="block" color="primary" sx={{ fontWeight: 600 }}>
                          {t('period.periodA', 'Período A')}: {fmtNum(heatmapData.stats.totalDetections)}
                        </Typography>
                        <Typography variant="caption" display="block" sx={{ color: '#ab47bc', fontWeight: 600 }}>
                          {t('period.periodB', 'Período B')}: {fmtNum(heatmapDataB.stats.totalDetections)}
                        </Typography>
                      </Box>
                      {(() => {
                        const totA = heatmapData.stats.totalDetections || 0;
                        const totB = heatmapDataB.stats.totalDetections || 0;
                        const diff = totA - totB;
                        const pct = totB > 0 ? Math.round((diff / totB) * 100) : (totA > 0 ? 100 : 0);
                        const isPos = diff >= 0;
                        return (
                          <Chip
                            label={`${isPos ? '+' : ''}${pct}%`}
                            size="small"
                            color={isPos ? "success" : "error"}
                            sx={{ fontWeight: 700, fontSize: '0.75rem' }}
                          />
                        );
                      })()}
                    </Stack>
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    textTransform: 'uppercase',
                    fontSize: 10,
                    letterSpacing: 0.9,
                    color: 'text.secondary',
                    mb: 1.5,
                  }}
                >
                  {t('stats.visitsByZone')}
                </Typography>

                {isCompareActive && heatmapDataB && comparisonZones.some(cz => cz.status === 'ONLY_A' || cz.status === 'ONLY_B') && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      mt: 1,
                      mb: 1.5,
                      p: 1,
                      borderRadius: 1,
                      bgcolor: 'rgba(255, 255, 255, 0.03)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                    }}
                  >
                    <InfoIcon sx={{ color: 'info.main', fontSize: 16, flexShrink: 0 }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: 11, lineHeight: 1.3 }}>
                      {t('stats.zoneMismatchNotice', 'Algumas zonas registaram dados em apenas um dos períodos comparados.')}
                    </Typography>
                  </Stack>
                )}

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                    maxHeight: { xs: 'none', lg: 540 },
                    overflowY: 'auto',
                    pr: 0.5,
                    '&::-webkit-scrollbar': { width: 5 },
                    '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2.5 },
                  }}
                >
                  {comparisonZones.map((cz) => {
                    const zone = cz.zoneA || cz.zoneB;
                    if (!zone) return null;

                    const dwellText = formatDwellTime(zone.avgDwellSeconds);
                    const ageData = getAgeLabelsAndValues(zone);
                    const totalGender = (zone.maleCount ?? 0) + (zone.femaleCount ?? 0);
                    const malePct = totalGender > 0 ? Math.round(((zone.maleCount ?? 0) / totalGender) * 100) : null;
                    const femalePct = totalGender > 0 ? 100 - malePct : null;
                    const isHovered = hoveredZoneIdx === cz.originalIdx;
                    const isSelected = zone.id && selectedZoneIds.some(id => String(id).toLowerCase() === String(zone.id).toLowerCase());
                    const badgeColor = getBadgeColor(zone.xovisLogicType).hex;

                    return (
                      <Box
                        key={cz.key}
                        ref={(el) => { zoneCardRefs.current[cz.originalIdx] = el; }}
                        onMouseEnter={() => setHoveredZoneIdx(cz.originalIdx)}
                        onMouseLeave={() => setHoveredZoneIdx(null)}
                        onClick={() => {
                          if (zone.id) setSelectedZoneIds([zone.id]);
                          zoneChartRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }}
                        sx={{
                          p: 1.5,
                          borderRadius: 1.5,
                          border: (isHovered || isSelected) ? `2px solid ${badgeColor}` : '1px solid rgba(255,255,255,0.08)',
                          bgcolor: (isHovered || isSelected) ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.02)',
                          cursor: 'pointer',
                          transition: 'all 0.15s ease',
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                          <Box
                            sx={{
                              width: 22,
                              height: 22,
                              borderRadius: '50%',
                              bgcolor: badgeColor,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              border: '1.5px solid rgba(255,255,255,0.7)',
                            }}
                          >
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#fff', lineHeight: 1 }}>{cz.badgeNum}</Typography>
                          </Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{cz.name}</Typography>

                          {isCompareActive && cz.status === 'ONLY_A' && (
                            <Tooltip title={t('stats.onlyPeriodATooltip', 'Esta zona apenas registou dados no 1.º Período (Período A). Ausente no Período B.')} arrow placement="top">
                              <Chip
                                icon={<InfoIcon sx={{ '&&': { color: '#64b5f6', fontSize: 13 } }} />}
                                label={t('stats.onlyPeriodA', 'Apenas Período A')}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.625rem',
                                  fontWeight: 600,
                                  bgcolor: 'rgba(33, 150, 243, 0.12)',
                                  color: '#64b5f6',
                                  border: '1px solid rgba(33, 150, 243, 0.3)',
                                  ml: 'auto',
                                }}
                              />
                            </Tooltip>
                          )}

                          {isCompareActive && cz.status === 'ONLY_B' && (
                            <Tooltip title={t('stats.onlyPeriodBTooltip', 'Esta zona apenas registou dados no 2.º Período (Período B). Ausente no Período A.')} arrow placement="top">
                              <Chip
                                icon={<InfoIcon sx={{ '&&': { color: '#ce93d8', fontSize: 13 } }} />}
                                label={t('stats.onlyPeriodB', 'Apenas Período B')}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.625rem',
                                  fontWeight: 600,
                                  bgcolor: 'rgba(156, 39, 176, 0.12)',
                                  color: '#ce93d8',
                                  border: '1px solid rgba(156, 39, 176, 0.3)',
                                  ml: 'auto',
                                }}
                              />
                            </Tooltip>
                          )}
                        </Stack>

                        {isCompareActive ? (
                          <Box sx={{ mt: 0.5 }}>
                            <Grid container spacing={1} sx={{ pb: 0.75, borderBottom: '1px solid rgba(255,255,255,0.08)', mb: 1 }}>
                              <Grid item xs={6}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#64b5f6', fontSize: 10.5, display: 'block' }}>
                                  {t('period.periodA', 'Período A').toUpperCase()}
                                </Typography>
                              </Grid>
                              <Grid item xs={6} sx={{ borderLeft: '1px solid rgba(255,255,255,0.08)', pl: 1 }}>
                                <Typography variant="caption" sx={{ fontWeight: 700, color: '#ce93d8', fontSize: 10.5, display: 'block' }}>
                                  {t('period.periodB', 'Período B').toUpperCase()}
                                </Typography>
                              </Grid>
                            </Grid>

                            {(() => {
                              const { zoneA, zoneB } = cz;
                              const rawType = cz.xovisLogicType || zoneA?.xovisLogicType || zoneB?.xovisLogicType || zoneA?.type || zoneB?.type || '';
                              const type = String(rawType).toUpperCase().trim();

                              const isAttention = type.includes('ATTENTION');
                              const isQueue = type.includes('QUEUE');
                              const isOccupancyCount = type.includes('OCCUPANCY');
                              const isZone = isZoneLogic(type);

                              const renderSplitRow = (label, valA, valB, formatFn = fmtNum, colorA = 'text.primary', colorB = 'text.primary') => {
                                const nA = typeof valA === 'number' && !isNaN(valA) ? valA : 0;
                                const nB = typeof valB === 'number' && !isNaN(valB) ? valB : 0;
                                const diff = nB - nA;
                                const pct = nA > 0 ? Math.round((diff / nA) * 100) : (nB > 0 ? 100 : 0);
                                const isPos = diff >= 0;

                                return (
                                  <Box key={label} sx={{ mb: 1, '&:last-child': { mb: 0 } }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 9.5, mb: 0.25, fontWeight: 500 }}>
                                      {label}
                                    </Typography>
                                    <Grid container spacing={1} alignItems="center">
                                      <Grid item xs={6}>
                                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 12, color: colorA }}>
                                          {formatFn(nA)}
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={6} sx={{ borderLeft: '1px solid rgba(255,255,255,0.06)', pl: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 12, color: colorB }}>
                                          {formatFn(nB)}
                                        </Typography>
                                        <Chip
                                          label={`${isPos ? '+' : ''}${pct}%`}
                                          size="small"
                                          color={isPos ? "success" : "error"}
                                          sx={{ height: 15, fontSize: '0.6rem', fontWeight: 700, px: 0.25 }}
                                        />
                                      </Grid>
                                    </Grid>
                                  </Box>
                                );
                              };

                              const metricRows = [];

                              if (isAttention) {
                                metricRows.push(renderSplitRow(t('stats.attentionUniqueVisitors', 'Visitantes Únicos'), zoneA?.cumulativeVisits, zoneB?.cumulativeVisits));
                                metricRows.push(renderSplitRow(t('stats.attentionUniqueViewers', 'Visualizadores Únicos'), zoneA?.inCount, zoneB?.inCount));
                                metricRows.push(renderSplitRow(t('stats.attentionAvgViewTime', 'Tempo Médio Visualização'), zoneA?.outCount, zoneB?.outCount, formatDwellTime));
                                if ((zoneA?.avgDwellSeconds ?? 0) > 0 || (zoneB?.avgDwellSeconds ?? 0) > 0) {
                                  metricRows.push(renderSplitRow(t('stats.attentionAvgDwellTime', 'Tempo Médio Permanência'), zoneA?.avgDwellSeconds, zoneB?.avgDwellSeconds, formatDwellTime));
                                }
                              } else if (isQueue) {
                                metricRows.push(renderSplitRow(t('stats.queueCurrentLength', 'Tamanho da Fila Atual'), zoneA?.occupancy, zoneB?.occupancy, fmtNum, 'warning.main', 'warning.main'));
                                metricRows.push(renderSplitRow(t('stats.queueOutflow', 'Fluxo de Saída'), zoneA?.outCount, zoneB?.outCount));
                                if ((zoneA?.avgDwellSeconds ?? 0) > 0 || (zoneB?.avgDwellSeconds ?? 0) > 0) {
                                  metricRows.push(renderSplitRow(t('stats.queueTotalTime', 'Tempo Total de Fila'), zoneA?.avgDwellSeconds, zoneB?.avgDwellSeconds, formatDwellTime));
                                }
                              } else if (isZone) {
                                metricRows.push(renderSplitRow(t('stats.totalVisits', 'Total de Visitas'), zoneA?.cumulativeVisits ?? zoneA?.count, zoneB?.cumulativeVisits ?? zoneB?.count));
                                if (isOccupancyCount) {
                                  metricRows.push(renderSplitRow(t('stats.currentOccupancy', 'Ocupação Atual'), zoneA?.occupancy, zoneB?.occupancy, fmtNum, 'warning.main', 'warning.main'));
                                }
                                if ((zoneA?.avgDwellSeconds ?? 0) > 0 || (zoneB?.avgDwellSeconds ?? 0) > 0) {
                                  metricRows.push(renderSplitRow(t('stats.avgDwellTime', 'Tempo Médio de Permanência'), zoneA?.avgDwellSeconds, zoneB?.avgDwellSeconds, formatDwellTime));
                                }
                              } else {
                                metricRows.push(renderSplitRow(t('stats.entradasIn', 'Entradas'), zoneA?.inCount, zoneB?.inCount, fmtNum, 'success.main', 'success.main'));
                                metricRows.push(renderSplitRow(t('stats.saidasOut', 'Saídas'), zoneA?.outCount, zoneB?.outCount, fmtNum, 'error.main', 'error.main'));
                              }

                              // Demographic Metrics: Gender
                              const maleA = zoneA?.maleCount ?? 0;
                              const maleB = zoneB?.maleCount ?? 0;
                              const femaleA = zoneA?.femaleCount ?? 0;
                              const femaleB = zoneB?.femaleCount ?? 0;

                              if (maleA > 0 || maleB > 0) {
                                metricRows.push(renderSplitRow(t('stats.maleCount', 'Homens (M)'), zoneA?.maleCount, zoneB?.maleCount, fmtNum, '#2196F3', '#2196F3'));
                              }
                              if (femaleA > 0 || femaleB > 0) {
                                metricRows.push(renderSplitRow(t('stats.femaleCount', 'Mulheres (F)'), zoneA?.femaleCount, zoneB?.femaleCount, fmtNum, '#E91E63', '#E91E63'));
                              }

                              // Demographic Metrics: Age Distribution
                              const ageDataA = zoneA ? getAgeLabelsAndValues(zoneA) : [];
                              const ageDataB = zoneB ? getAgeLabelsAndValues(zoneB) : [];

                              const ageMapA = new Map(ageDataA.map(a => [a.label, a.value]));
                              const ageMapB = new Map(ageDataB.map(a => [a.label, a.value]));

                              const defaultAgeLabels = ['< 16', '16 - 40', '40 - 65', '> 65'];
                              const allAgeLabels = Array.from(new Set([
                                ...defaultAgeLabels,
                                ...ageDataA.map(a => a.label),
                                ...ageDataB.map(a => a.label)
                              ]));

                              allAgeLabels.forEach(label => {
                                const vA = ageMapA.get(label) ?? 0;
                                const vB = ageMapB.get(label) ?? 0;
                                metricRows.push(renderSplitRow(`${t('stats.ageGroup', 'Idade')} ${label}`, vA, vB));
                              });

                              return metricRows;
                            })()}
                          </Box>
                        ) : (
                          <>
                            {zone.xovisLogicType === 'XLT_ATTENTION' ? (
                              <Grid container spacing={1.5}>
                                <Grid item xs={6}>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 10 }}>
                                    {t('stats.attentionUniqueVisitors')}
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {fmtNum(zone.cumulativeVisits ?? 0)}
                                  </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 10 }}>
                                    {t('stats.attentionUniqueViewers')}
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {fmtNum(zone.inCount ?? 0)}
                                  </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 10 }}>
                                    {t('stats.attentionAvgViewTime')}
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {formatDwellTime(zone.outCount) || '0s'}
                                  </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 10 }}>
                                    {t('stats.attentionAvgDwellTime')}
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {dwellText || '0s'}
                                  </Typography>
                                </Grid>
                              </Grid>
                            ) : zone.xovisLogicType === 'XLT_QUEUE_STATISTICS' ? (
                              <Grid container spacing={1.5}>
                                <Grid item xs={6}>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 10 }}>
                                    {t('stats.queueCurrentLength')}
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'warning.main' }}>
                                    {fmtNum(zone.occupancy ?? 0)}
                                  </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 10 }}>
                                    {t('stats.queueOutflow')}
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {fmtNum(zone.outCount ?? 0)}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 10 }}>
                                    {t('stats.queueTotalTime')}
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {dwellText || '0s'}
                                  </Typography>
                                </Grid>
                              </Grid>
                            ) : isZoneLogic(zone.xovisLogicType) ? (
                              <Box>
                                {zone.xovisLogicType === 'XLT_ZONE_OCCUPANCY_COUNT' ? (
                                  <Grid container spacing={1}>
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 10 }}>
                                        {t('stats.totalVisits')}
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {fmtNum(zone.cumulativeVisits ?? 0)}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 10 }}>
                                        {t('stats.currentOccupancy')}
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'warning.main' }}>
                                        {fmtNum(zone.occupancy ?? 0)}
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                ) : (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 10 }}>
                                      {t('stats.totalVisits')}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      {fmtNum(zone.cumulativeVisits ?? 0)}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            ) : (
                              <Grid container spacing={1}>
                                <Grid item xs={6}>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 10 }}>
                                    {t('stats.entradasIn')}
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                                    {fmtNum(zone.inCount ?? 0)}
                                  </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: 10 }}>
                                    {t('stats.saidasOut')}
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
                                    {fmtNum(zone.outCount ?? 0)}
                                  </Typography>
                                </Grid>
                              </Grid>
                            )}

                            {((dwellText && isZoneLogic(zone.xovisLogicType) && zone.xovisLogicType !== 'XLT_ATTENTION' && zone.xovisLogicType !== 'XLT_QUEUE_STATISTICS') || totalGender > 0 || ageData.length > 0) && (
                              <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed rgba(255,255,255,0.06)', display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {dwellText && isZoneLogic(zone.xovisLogicType) && zone.xovisLogicType !== 'XLT_ATTENTION' && zone.xovisLogicType !== 'XLT_QUEUE_STATISTICS' && (
                                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="caption" color="text.secondary">{t('stats.avgDwellTime')}</Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>{dwellText}</Typography>
                                  </Stack>
                                )}

                                {totalGender > 0 && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                                      {t('stats.genderDistribution')}
                                    </Typography>
                                    {zone.isDemographicCumulative && (
                                      <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontSize: 10, fontStyle: 'italic', color: '#f39100', opacity: 0.85 }}>
                                        {t('stats.cumulativeWarning')}
                                      </Typography>
                                    )}
                                    <Box sx={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', bgcolor: 'rgba(255,255,255,0.1)' }}>
                                      <Box sx={{ width: `${malePct}%`, bgcolor: '#2196F3' }} title={`Male: ${malePct}%`} />
                                      <Box sx={{ width: `${femalePct}%`, bgcolor: '#E91E63' }} title={`Female: ${femalePct}%`} />
                                    </Box>
                                    <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.25 }}>
                                      <Typography variant="caption" sx={{ fontSize: 9, color: '#2196F3' }}>M: {malePct}%</Typography>
                                      <Typography variant="caption" sx={{ fontSize: 9, color: '#E91E63' }}>F: {femalePct}%</Typography>
                                    </Stack>
                                  </Box>
                                )}

                                {ageData.length > 0 && (ageData.some(a => a.value > 0) || totalGender > 0 || zone.isDemographicCumulative) && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>{t('stats.ageDistribution')}</Typography>
                                    {zone.isDemographicCumulative && (
                                      <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontSize: 10, fontStyle: 'italic', color: '#f39100', opacity: 0.85 }}>{t('stats.cumulativeWarning')}</Typography>
                                    )}
                                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                                      {ageData.map(age => (
                                        <Box key={age.label} sx={{ px: 0.75, py: 0.25, borderRadius: 0.5, bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.05)', display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                          <Typography variant="caption" sx={{ fontSize: 9, color: 'text.secondary' }}>{age.label}:</Typography>
                                          <Typography variant="caption" sx={{ fontSize: 9, fontWeight: 600 }}>{fmtNum(age.value)}</Typography>
                                        </Box>
                                      ))}
                                    </Stack>
                                  </Box>
                                )}
                              </Box>
                            )}
                          </>
                        )}
                      </Box>
                    );
                  })}
                  {comparisonZones.length === 0 && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', py: 1 }}>
                      {t('stats.noZones', 'Sem zonas configuradas')}
                    </Typography>
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    textTransform: 'uppercase',
                    fontSize: 10,
                    letterSpacing: 0.9,
                    color: 'text.secondary',
                    mb: 0.75,
                  }}
                >
                  {t('stats.mostVisited')}
                </Typography>
                <Stack
direction="row"
alignItems="center"
gap={0.5}
                >
                  <TrendingUpIcon sx={{ fontSize: 17, color: 'primary.main' }} />
                  <Typography
variant="body2"
sx={{ fontWeight: 700, color: 'primary.main' }}
                  >
                    {heatmapData.stats.mostVisited}
                  </Typography>
                </Stack>
              </>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 220,
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                  sx={{ px: 2 }}
                >
                  {t('stats.selectDevice')}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* ── Zone Metrics Chart & Temporal Breakdown ── */}
      {deviceId && (
        <Box ref={zoneChartRef} sx={{ mt: 3 }}>
          <Card sx={{ p: 2.5 }}>
            {/* Header: Title + Zone Selector */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5} sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUpIcon color="primary" />
                {t('stats.zoneTrafficOverTime', 'Tráfego por Zona ao Longo do Tempo')}
              </Typography>

              <Box onMouseLeave={() => setHeatmapZoneMenuOpen(false)}>
                <FormControl size="small" sx={{ minWidth: 220, maxWidth: 360 }}>
                  <Select
                    multiple
                    open={heatmapZoneMenuOpen}
                    onOpen={() => setHeatmapZoneMenuOpen(true)}
                    onClose={() => setHeatmapZoneMenuOpen(false)}
                    MenuProps={{
                      PaperProps: {
                        onMouseLeave: () => setHeatmapZoneMenuOpen(false),
                      },
                    }}
                    value={selectedZoneIds}
                    onChange={e => {
                      const val = e.target.value;
                      if (Array.isArray(val) && val.includes('__ALL__')) {
                        if (selectedZoneIds.length === availableZones.length) {
                          setSelectedZoneIds([]);
                        } else {
                          setSelectedZoneIds(availableZones.map(z => z.zoneId));
                        }
                        return;
                      }
                      setSelectedZoneIds(typeof val === 'string' ? val.split(',') : val);
                    }}
                    displayEmpty
                    renderValue={selected => {
                      if (!selected || selected.length === 0) {
                        return <em style={{ opacity: 0.6 }}>{t('stats.noZoneSelected', 'Nenhuma zona selecionada')}</em>;
                      }
                      if (availableZones.length > 0 && selected.length === availableZones.length) {
                        return t('stats.allZones', 'Todas as Zonas');
                      }
                      const names = availableZones
                        .filter(z => selected.some(id => String(id).toLowerCase() === String(z.zoneId).toLowerCase()))
                        .map(z => z.zoneName);
                      return names.join(', ');
                    }}
                    sx={{ fontSize: '0.8125rem', height: 40 }}
                  >
                    {availableZones.length > 0 && (
                      <MenuItem value="__ALL__">
                        <Checkbox checked={selectedZoneIds.length === availableZones.length} size="small" />
                        <ListItemText primary={t('stats.selectAll', 'Selecionar Todas')} primaryTypographyProps={{ fontSize: '0.8125rem', fontWeight: 600 }} />
                      </MenuItem>
                    )}
                    {availableZones.length > 0 && <Divider />}
                    {availableZones.map(z => {
                      const isSelected = selectedZoneIds.some(id => String(id).toLowerCase() === String(z.zoneId).toLowerCase());
                      return (
                        <MenuItem key={z.zoneId} value={z.zoneId}>
                          <Checkbox checked={isSelected} size="small" />
                          <ListItemText
                            primary={
                              (z.parentZoneName
                                ? `${z.zoneName} (${z.xovisLogicType || 'LOGIC'} → ${z.parentZoneName})`
                                : (z.xovisLogicType ? `${z.zoneName} (${z.xovisLogicType})` : z.zoneName)) +
                              (z.isActive ? '' : t('stats.inactive', ' [Inativa]'))
                            }
                            primaryTypographyProps={{ fontSize: '0.8125rem', color: z.isActive ? 'text.primary' : 'text.secondary' }}
                          />
                        </MenuItem>
                      );
                    })}
                    {availableZones.length === 0 && (
                      <MenuItem disabled sx={{ fontSize: '0.8125rem' }}>
                        <em>{t('stats.noAvailableZones', 'Sem zonas disponíveis')}</em>
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Box>
            </Stack>

            {/* Controls: Preset Period + Custom Date Picker */}
            <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.5} sx={{ mb: 2.5 }}>
              <ToggleButtonGroup
                value={period}
                exclusive
                onChange={(_, v) => { if (v) setPeriod(v); }}
                size="small"
                sx={TOGGLE_SX}
              >
                <ToggleButton value="today">{t('period.today', 'Hoje')}</ToggleButton>
                <ToggleButton value="7d">{t('period.7d', '7 Dias')}</ToggleButton>
                <ToggleButton value="30d">{t('period.30d', '30 Dias')}</ToggleButton>
                <ToggleButton value="month">{t('period.month', 'Mês')}</ToggleButton>
                <ToggleButton value="custom">{t('period.custom', 'Personalizado')}</ToggleButton>
                <ToggleButton value="allTime">{t('period.allTime', 'Desde sempre')}</ToggleButton>
              </ToggleButtonGroup>

              {period === 'custom' && (
                <DateRangePopover
                  startDate={startDate}
                  endDate={endDate}
                  onStartChange={handleCustomStartDate}
                  onEndChange={handleCustomEndDate}
                  startLabel={t('period.dateStart', 'Data Inicial')}
                  endLabel={t('period.dateEnd', 'Data Final')}
                />
              )}
            </Stack>

            {/* Chart visualization */}
            <VegaTrafficChart
              data={selectedZoneSeries}
              granularity={committedGranularity}
              onGranularityChange={setGranularity}
              isToday={committedPeriod === 'today'}
              period={committedPeriod}
              startDate={committedStartDate}
              endDate={committedEndDate}
              height={360}
              hideDwellOverlay
            />
          </Card>
        </Box>
      )}

      <AutoCadAlignModal
        open={autoCadOpen}
        onClose={() => setAutoCadOpen(false)}
        deviceId={deviceId}
        bgImage={bgImage}
        heatmapData={heatmapData}
        selectedDevice={selectedDevice}
        setBgImageVersion={setBgImageVersion}
        setDevices={setDevices}
      />

      {/* ── Fullscreen Heatmap Dialog ── */}
      <HeatmapFullscreenDialog
        open={isFullscreen}
        onClose={() => setIsFullscreen(false)}
        heatmapData={heatmapData}
        bgImage={bgImage}
        hoveredZoneIdx={hoveredZoneIdx}
        onZoneHover={(idx) => setHoveredZoneIdx(idx)}
        HeatmapCanvasComponent={HeatmapCanvas}
      />
    </Box>
  );
};

export default HeatmapViewerPage;
